import React, { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../../components/AuthContext';
import axios from 'axios';
import { FaFileInvoice, FaFileAlt, FaTruck, FaChartLine, FaCheckCircle, FaClock, FaTimesCircle, FaCalendarAlt, FaRupeeSign } from 'react-icons/fa';
import { Line, Bar, Doughnut, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Helper function to format numbers with commas
const formatNumberWithCommas = (number) => {
  // Handle null, undefined, or string values
  const value = typeof number === 'string' ? parseFloat(number) : number;
  
  // Return 0.00 if the value is not a valid number
  if (!value || isNaN(value)) {
    return '0.00';
  }

  return new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2
  }).format(value);
};

// Helper function to format IDs (from TallyQuotation.js)
const formatId = (id) => {
  if (id === null || id === undefined) return '';
  
  // If it's already a string, return it
  if (typeof id === 'string') return id;
  
  // Handle different object formats
  if (typeof id === 'object') {
    // For MongoDB ObjectId representation
    if (id.number !== undefined) return String(id.number);
    if (id._id !== undefined) return String(id._id);
    if (id.id !== undefined) return String(id.id);
    
    // For numeric values that might be objects
    if (id.toString && typeof id.toString === 'function') {
      return id.toString();
    }
    
    // Last resort - try to stringify
    try {
      return JSON.stringify(id);
    } catch (e) {
      return '[Object]';
    }
  }
  
  // For any other type, convert to string
  return String(id);
};

function Dashboard({ dashboardAccess = [] }) {
    const { user } = useContext(AuthContext);
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [loading, setLoading] = useState(true);
    
    // Get user's role-based access
    const userRoleAccess = user?.user?.role?.dashboardAccess || [];
    
    // Check if specific modules have BOTH domain AND role access
    const hasDeliveryOrderAccess = dashboardAccess.includes('delivery-order') && userRoleAccess.includes('delivery-order');
    const hasQuotationNewAccess = dashboardAccess.includes('quotation-new') && userRoleAccess.includes('quotation-new');
    const hasTallyQuotationAccess = dashboardAccess.includes('tally-quotation') && userRoleAccess.includes('tally-quotation');
    const hasTallyItemsAccess = dashboardAccess.includes('tally-items-management') && userRoleAccess.includes('tally-items-management');
    const hasTallyLedgerAccess = dashboardAccess.includes('tally-ledger-management') && userRoleAccess.includes('tally-ledger-management');
    
    // Debug logging
    console.log('Dashboard - Domain Access:', dashboardAccess);
    console.log('Dashboard - Role Access:', userRoleAccess);
    console.log('Dashboard - hasDeliveryOrderAccess (both checks):', hasDeliveryOrderAccess);
    console.log('Dashboard - hasQuotationNewAccess (both checks):', hasQuotationNewAccess);
    console.log('Dashboard - hasTallyQuotationAccess (both checks):', hasTallyQuotationAccess);
    console.log('Dashboard - hasTallyItemsAccess (both checks):', hasTallyItemsAccess);
    console.log('Dashboard - hasTallyLedgerAccess (both checks):', hasTallyLedgerAccess);
    
    // Delivery Orders State
    const [deliveryOrders, setDeliveryOrders] = useState([]);
    const [deliveryOrderStats, setDeliveryOrderStats] = useState({
        total: 0,
        pending: 0,
        completed: 0,
        cancelled: 0,
        totalAmount: 0,
        weeklyData: []
    });
    const [deliveryOrdersChartData, setDeliveryOrdersChartData] = useState({ createdBy: [], salesman: [] }); // For pie charts

    // Quotation New State
    const [quotations, setQuotations] = useState([]);
    const [quotationStats, setQuotationStats] = useState({
        total: 0,
        pending: 0,
        completed: 0,
        cancelled: 0,
        partialComplete: 0,
        refFollow: 0,
        totalValue: 0,
        weeklyData: []
    });
    const [quotationsChartData, setQuotationsChartData] = useState({ createdBy: [], salesman: [] }); // For pie charts

    // Tally Quotation State
    const [tallyQuotations, setTallyQuotations] = useState([]);
    const [tallyQuotationStats, setTallyQuotationStats] = useState({
        total: 0,
        pending: 0,
        completed: 0,
        partialComplete: 0,
        refFollow: 0,
        cancelled: 0,
        totalValue: 0,
        weeklyData: []
    });
    const [tallyQuotationsChartData, setTallyQuotationsChartData] = useState({ createdBy: [], salesman: [] }); // For pie charts

    // Tally Items State
    const [tallyItemsStats, setTallyItemsStats] = useState({
        total: 0,
        withBrands: 0,
        withParameters: 0,
        withImages: 0,
        last7Days: []
    });

    // Tally Ledger State
    const [tallyLedgerStats, setTallyLedgerStats] = useState({
        total: 0,
        last7Days: []
    });

    // Pagination State
    const [deliveryOrderPage, setDeliveryOrderPage] = useState(1);
    const [quotationPage, setQuotationPage] = useState(1);
    const [tallyQuotationPage, setTallyQuotationPage] = useState(1);
    const itemsPerPage = 10;

    // Total counts for pagination
    const [deliveryOrderTotal, setDeliveryOrderTotal] = useState(0);
    const [quotationTotal, setQuotationTotal] = useState(0);
    const [tallyQuotationTotal, setTallyQuotationTotal] = useState(0);

    // Modal State
    const [showModal, setShowModal] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);

    // Search and Filter State (temp values for input)
    const [deliveryOrderSearchTemp, setDeliveryOrderSearchTemp] = useState('');
    const [quotationSearchTemp, setQuotationSearchTemp] = useState('');
    const [tallyQuotationSearchTemp, setTallyQuotationSearchTemp] = useState('');
    const [quotationDateRangeTemp, setQuotationDateRangeTemp] = useState({ startDate: '', endDate: '' });
    const [tallyQuotationDateRangeTemp, setTallyQuotationDateRangeTemp] = useState({ startDate: '', endDate: '' });
    
    // Applied filters (actual values used in API calls)
    const [deliveryOrderSearch, setDeliveryOrderSearch] = useState('');
    const [quotationSearch, setQuotationSearch] = useState('');
    const [tallyQuotationSearch, setTallyQuotationSearch] = useState('');
    const [quotationDateRange, setQuotationDateRange] = useState({ startDate: '', endDate: '' });
    const [tallyQuotationDateRange, setTallyQuotationDateRange] = useState({ startDate: '', endDate: '' });

    // Check dark mode
    useEffect(() => {
        const checkDarkMode = () => {
            const darkMode = localStorage.getItem('darkMode') === 'true';
            setIsDarkMode(darkMode);
        };
        
        checkDarkMode();
        const interval = setInterval(checkDarkMode, 500);
        return () => clearInterval(interval);
    }, []);

    // Fetch all data
    useEffect(() => {
        const fetchAllData = async () => {
            setLoading(true);
            try {
                const companyId = user.user.company._id || user.user.company;
                console.log('Dashboard fetchAllData - companyId:', companyId);
                
                if (!companyId) {
                    console.error('Company ID is missing from user object');
                    setLoading(false);
                    return;
                }
                
                // Fetch data only for modules with access
                const fetchPromises = [];
                
                if (hasDeliveryOrderAccess) {
                    fetchPromises.push(fetchDeliveryOrders(companyId, deliveryOrderPage));
                }
                if (hasQuotationNewAccess) {
                    fetchPromises.push(fetchQuotations(companyId, quotationPage));
                }
                if (hasTallyQuotationAccess) {
                    fetchPromises.push(fetchTallyQuotations(companyId, tallyQuotationPage));
                }
                if (hasTallyItemsAccess) {
                    fetchPromises.push(fetchTallyItemsStats(companyId));
                }
                if (hasTallyLedgerAccess) {
                    fetchPromises.push(fetchTallyLedgerStats(companyId));
                }
                
                await Promise.all(fetchPromises);
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
            } finally {
                setLoading(false);
            }
        };
        
        fetchAllData();
    }, [user, deliveryOrderPage, quotationPage, tallyQuotationPage, hasDeliveryOrderAccess, hasQuotationNewAccess, hasTallyQuotationAccess, hasTallyItemsAccess, hasTallyLedgerAccess, deliveryOrderSearch, quotationSearch, tallyQuotationSearch, quotationDateRange, tallyQuotationDateRange]);

    // Refetch delivery orders when page or applied search changes
    useEffect(() => {
        if (!loading && user && hasDeliveryOrderAccess) {
            const companyId = user.user.company._id || user.user.company;
            fetchDeliveryOrders(companyId, deliveryOrderPage);
        }
    }, [deliveryOrderPage, deliveryOrderSearch]);

    // Refetch quotations when page, applied search, or applied date range changes
    useEffect(() => {
        if (!loading && user && hasQuotationNewAccess) {
            const companyId = user.user.company._id || user.user.company;
            fetchQuotations(companyId, quotationPage);
        }
    }, [quotationPage, quotationSearch, quotationDateRange]);

    // Refetch tally quotations when page, applied search, or applied date range changes
    useEffect(() => {
        if (!loading && user && hasTallyQuotationAccess) {
            const companyId = user.user.company._id || user.user.company;
            fetchTallyQuotations(companyId, tallyQuotationPage);
        }
    }, [tallyQuotationPage]);

    const fetchDeliveryOrders = async (companyId, page = 1) => {
        try {
            // Build query params
            let queryParams = `page=${page}&limit=${itemsPerPage}&companyId=${companyId}`;
            if (deliveryOrderSearch) {
                queryParams += `&search=${encodeURIComponent(deliveryOrderSearch)}`;
            }
            
            const response = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/delivery-orders/dashboard-recent?${queryParams}`
            );
            const result = response.data;
            setDeliveryOrders(result.orders || []);
            setDeliveryOrderTotal(result.count || 0);
            
            // Calculate stats
            const statsResponse = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/delivery-orders/dashboard-stats?companyId=${companyId}`
            );
            setDeliveryOrderStats(statsResponse.data || {
                total: result.count || 0,
                pending: 0,
                completed: 0,
                cancelled: 0,
                totalAmount: 0,
                weeklyData: []
            });

            // Fetch pie chart data from backend
            const [createdByResponse, salesmanResponse] = await Promise.all([
                axios.get(`${process.env.REACT_APP_API_BASE_URL}/delivery-orders/dashboard-created-by?companyId=${companyId}`),
                axios.get(`${process.env.REACT_APP_API_BASE_URL}/delivery-orders/dashboard-salesman?companyId=${companyId}`)
            ]);
            
            setDeliveryOrdersChartData({
                createdBy: createdByResponse.data || [],
                salesman: salesmanResponse.data || []
            });
        } catch (error) {
            console.error('Error fetching delivery orders:', error);
        }
    };

    const fetchQuotations = async (companyId, page = 1) => {
        try {
            // Build query params
            let queryParams = `page=${page}&limit=${itemsPerPage}&companyId=${companyId}`;
            if (quotationSearch) {
                queryParams += `&search=${encodeURIComponent(quotationSearch)}`;
            }
            if (quotationDateRange.startDate) {
                queryParams += `&startDate=${quotationDateRange.startDate}`;
            }
            if (quotationDateRange.endDate) {
                queryParams += `&endDate=${quotationDateRange.endDate}`;
            }
            
            const response = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/quotation-new/all?${queryParams}`
            );
            const result = response.data;
            setQuotations(result.quotations || []);
            setQuotationTotal(result.count || 0);
            
            // Calculate stats
            const statsResponse = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/quotation-new/stats?companyId=${companyId}`
            );
            setQuotationStats(statsResponse.data || {
                total: result.count || 0,
                pending: 0,
                completed: 0,
                cancelled: 0,
                partialComplete: 0,
                refFollow: 0,
                totalValue: 0,
                weeklyData: []
            });

            // Fetch pie chart data from backend
            const [createdByResponse, salesmanResponse] = await Promise.all([
                axios.get(`${process.env.REACT_APP_API_BASE_URL}/quotation-new/dashboard-created-by?companyId=${companyId}`),
                axios.get(`${process.env.REACT_APP_API_BASE_URL}/quotation-new/dashboard-salesman?companyId=${companyId}`)
            ]);
            
            setQuotationsChartData({
                createdBy: createdByResponse.data || [],
                salesman: salesmanResponse.data || []
            });
        } catch (error) {
            console.error('Error fetching quotations:', error);
        }
    };

    const fetchTallyQuotations = async (companyId, page = 1) => {
        try {
            // Build query params
            let queryParams = `page=${page}&limit=${itemsPerPage}&companyId=${companyId}`;
            if (tallyQuotationSearch) {
                queryParams += `&search=${encodeURIComponent(tallyQuotationSearch)}`;
            }
            if (tallyQuotationDateRange.startDate) {
                queryParams += `&startDate=${tallyQuotationDateRange.startDate}`;
            }
            if (tallyQuotationDateRange.endDate) {
                queryParams += `&endDate=${tallyQuotationDateRange.endDate}`;
            }
            
            const response = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/tally-quotation/all?${queryParams}`
            );
            const result = response.data;
            setTallyQuotations(result.quotations || []);
            setTallyQuotationTotal(result.count || 0);
            
            // Calculate stats
            const statsResponse = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/tally-quotation/stats?companyId=${companyId}`
            );
            setTallyQuotationStats(statsResponse.data || {
                total: result.count || 0,
                pending: 0,
                completed: 0,
                partialComplete: 0,
                refFollow: 0,
                cancelled: 0,
                totalValue: 0,
                weeklyData: []
            });

            // Fetch pie chart data from backend
            const [createdByResponse, salesmanResponse] = await Promise.all([
                axios.get(`${process.env.REACT_APP_API_BASE_URL}/tally-quotation/dashboard-created-by?companyId=${companyId}`),
                axios.get(`${process.env.REACT_APP_API_BASE_URL}/tally-quotation/dashboard-salesman?companyId=${companyId}`)
            ]);
            
            setTallyQuotationsChartData({
                createdBy: createdByResponse.data || [],
                salesman: salesmanResponse.data || []
            });
        } catch (error) {
            console.error('Error fetching tally quotations:', error);
        }
    };

    const fetchTallyItemsStats = async (companyId) => {
        try {
            if (!companyId) {
                console.error('Company ID is undefined in fetchTallyItemsStats');
                return;
            }
            const response = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/tally/tally-items/dashboard-stats?companyId=${companyId}`
            );
            setTallyItemsStats(response.data || {
                total: 0,
                withBrands: 0,
                withParameters: 0,
                withImages: 0,
                last7Days: []
            });
        } catch (error) {
            console.error('Error fetching tally items stats:', error);
        }
    };

    const fetchTallyLedgerStats = async (companyId) => {
        try {
            if (!companyId) {
                console.error('Company ID is undefined in fetchTallyLedgerStats');
                return;
            }
            const response = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/tally-ledgers/dashboard-stats?companyId=${companyId}`
            );
            setTallyLedgerStats(response.data || {
                total: 0,
                last7Days: []
            });
        } catch (error) {
            console.error('Error fetching tally ledger stats:', error);
        }
    };

    // Apply filter handlers
    const handleApplyDeliveryOrderFilters = () => {
        setDeliveryOrderSearch(deliveryOrderSearchTemp);
        setDeliveryOrderPage(1);
    };

    const handleApplyQuotationFilters = () => {
        setQuotationSearch(quotationSearchTemp);
        setQuotationDateRange(quotationDateRangeTemp);
        setQuotationPage(1);
    };

    const handleApplyTallyQuotationFilters = () => {
        setTallyQuotationSearch(tallyQuotationSearchTemp);
        setTallyQuotationDateRange(tallyQuotationDateRangeTemp);
        setTallyQuotationPage(1);
    };

    const handleClearDeliveryOrderFilters = () => {
        setDeliveryOrderSearchTemp('');
        setDeliveryOrderSearch('');
        setDeliveryOrderPage(1);
    };

    const handleClearQuotationFilters = () => {
        setQuotationSearchTemp('');
        setQuotationDateRangeTemp({ startDate: '', endDate: '' });
        setQuotationSearch('');
        setQuotationDateRange({ startDate: '', endDate: '' });
        setQuotationPage(1);
    };

    const handleClearTallyQuotationFilters = () => {
        setTallyQuotationSearchTemp('');
        setTallyQuotationDateRangeTemp({ startDate: '', endDate: '' });
        setTallyQuotationSearch('');
        setTallyQuotationDateRange({ startDate: '', endDate: '' });
        setTallyQuotationPage(1);
    };

    // View record details
    const handleViewRecord = async (record, type) => {
        setShowModal(true);

        try {
            // Fetch full record with populated fields
            let fullRecord = record;
            if (type === 'delivery') {
                try {
                    const response = await axios.get(
                        `${process.env.REACT_APP_API_BASE_URL}/delivery-orders/details/${record._id}`
                    );
                    fullRecord = response.data.deliveryOrder || response.data || record;
                    
                    // Transform items to products array for consistent structure
                    if (fullRecord.items && fullRecord.items.length > 0) {
                        fullRecord.products = fullRecord.items.map(item => ({
                            item: item.itemId, // The populated itemId becomes item
                            Name: item.Name,
                            name: item.Name || item.itemId?.Name || item.itemId?.name,
                            description: item.description || '-',
                            itemDescription: item.description,
                            hsn: item.itemId?.hsnCode || '-',
                            hsnCode: item.itemId?.hsnCode,
                            quantity: item.quantity || 0,
                            units: item.units || 'box',
                            unit: item.units,
                            rate: item.rate || 0,
                            unitPrice: item.rate,
                            price: item.rate,
                            discount: item.discount || 0,
                            printType: item.printType,
                            amountBeforeDiscount: item.amountBeforeDiscount || 0,
                            amountAfterDiscount: item.amountAfterDiscount || 0,
                            totalPrice: item.amountAfterDiscount,
                            amount: item.amountAfterDiscount,
                            tt: item.tt
                        }));
                    }
                } catch (err) {
                    console.error('Error fetching full delivery order:', err);
                    fullRecord = record; // Fall back to existing record
                }
            } else if (type === 'quotation') {
                try {
                    const response = await axios.get(
                        `${process.env.REACT_APP_API_BASE_URL}/quotation-new/data/${record._id}`
                    );
                    fullRecord = response.data || record;
                    
                    // Transform sections into products array for Items Table
                    if (fullRecord.sections && fullRecord.sections.length > 0) {
                        fullRecord.products = fullRecord.sections.flatMap(section => 
                            section.items
                                .filter(item => item && (item.item || item.customName)) // Skip items without valid data
                                .map(item => ({
                                    name: item.customName || item.item?.skuDescription || item.item?.skuCode || 'N/A',
                                    productName: item.customName || item.item?.skuDescription,
                                    itemName: item.item?.skuDescription || item.item?.skuCode,
                                    description: item.narration || item.applicationArea || '-',
                                    itemDescription: item.narration,
                                    hsn: item.item?.hsnCode || '-',
                                    hsnCode: item.item?.hsnCode,
                                    quantity: item.quantity || 0,
                                    unit: item.unit || 'pcs',
                                    listedPrice: item.listedPrice || 0, // Original price before discount
                                    unitPrice: item.offerPrice || item.listedPrice || 0,
                                    price: item.offerPrice,
                                    rate: item.offerPrice, // Final price after discount
                                    discount: item.discount || 0,
                                    ttAmount: item.discount > 0 ? (item.quantity * item.listedPrice * item.discount / 100) : 0,
                                    totalPrice: item.offerPriceTotal || (item.quantity * item.offerPrice) || 0,
                                    amount: item.offerPriceTotal,
                                    printType: null, // QuotationNew doesn't have printType
                                    offerType: item.offerType,
                                    applicationArea: item.applicationArea,
                                    coverageArea: item.coverageArea,
                                    coverageAreaUnit: item.coverageAreaUnit,
                                    boxes: item.boxes,
                                    pieces: item.pieces,
                                    skuCode: item.item?.skuCode,
                                    brand: item.item?.brand,
                                    category: item.item?.category
                                }))
                        );
                    }
                } catch (err) {
                    console.error('Error fetching full quotation:', err);
                    fullRecord = record; // Fall back to existing record
                }
            } else if (type === 'tally') {
                try {
                    const response = await axios.get(
                        `${process.env.REACT_APP_API_BASE_URL}/tally-quotation/${record._id}`
                    );
                    fullRecord = response.data || record;
                } catch (err) {
                    console.error('Error fetching full tally quotation:', err);
                    fullRecord = record; // Fall back to existing record
                }
            }

            setSelectedRecord({ ...fullRecord, type });
        } catch (error) {
            console.error('Error fetching record details:', error);
            // Still set the record even if fetch fails
            setSelectedRecord({ ...record, type });
        }
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedRecord(null);
    };

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    // Chart configurations
    const getLineChartData = (weeklyData, label) => ({
        labels: weeklyData.map(d => d.label),
        datasets: [{
            label: label,
            data: weeklyData.map(d => d.count),
            borderColor: isDarkMode ? 'rgb(96, 165, 250)' : 'rgb(59, 130, 246)',
            backgroundColor: isDarkMode ? 'rgba(96, 165, 250, 0.1)' : 'rgba(59, 130, 246, 0.1)',
            tension: 0.4,
            fill: true,
            pointRadius: 4,
            pointHoverRadius: 6
        }]
    });

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                backgroundColor: isDarkMode ? 'rgba(31, 41, 55, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                titleColor: isDarkMode ? '#f3f4f6' : '#111827',
                bodyColor: isDarkMode ? '#d1d5db' : '#374151',
                borderColor: isDarkMode ? '#4b5563' : '#e5e7eb',
                borderWidth: 1
            }
        },
        scales: {
            x: {
                grid: {
                    display: false,
                    color: isDarkMode ? 'rgba(75, 85, 99, 0.3)' : 'rgba(229, 231, 235, 0.3)'
                },
                ticks: {
                    color: isDarkMode ? '#9ca3af' : '#6b7280'
                }
            },
            y: {
                beginAtZero: true,
                grid: {
                    color: isDarkMode ? 'rgba(75, 85, 99, 0.3)' : 'rgba(229, 231, 235, 0.3)'
                },
                ticks: {
                    color: isDarkMode ? '#9ca3af' : '#6b7280'
                }
            }
        }
    };

    const getDoughnutChartData = (stats, labels) => {
        let data = [];
        let backgroundColor = [];
        let borderColor = [];
        
        if (labels.includes('Partial Complete')) {
            // Quotations (New & Tally): Pending, Completed, Partial Complete, Ref Follow, Cancelled
            // Labels order: ['Pending', 'Completed', 'Partial Complete', 'Ref Follow', 'Cancelled']
            data = [
                stats.pending || 0,         // Index 0 - Pending - Yellow
                stats.completed || 0,       // Index 1 - Completed - Green
                stats.partialComplete || 0, // Index 2 - Partial Complete - Blue
                stats.refFollow || 0,       // Index 3 - Ref Follow - Purple
                stats.cancelled || 0        // Index 4 - Cancelled - Red
            ];
            backgroundColor = [
                'rgba(251, 191, 36, 0.8)',  // Index 0 - Yellow - Pending
                'rgba(34, 197, 94, 0.8)',   // Index 1 - Green - Completed
                'rgba(59, 130, 246, 0.8)',  // Index 2 - Blue - Partial Complete
                'rgba(147, 51, 234, 0.8)',  // Index 3 - Purple - Ref Follow
                'rgba(239, 68, 68, 0.8)'    // Index 4 - Red - Cancelled
            ];
            borderColor = [
                'rgba(251, 191, 36, 1)',    // Yellow
                'rgba(34, 197, 94, 1)',     // Green
                'rgba(59, 130, 246, 1)',    // Blue
                'rgba(147, 51, 234, 1)',    // Purple
                'rgba(239, 68, 68, 1)'      // Red
            ];
        } else if (labels.includes('Completed') && labels.includes('Cancelled')) {
            // Delivery Orders: Completed, Pending, Cancelled
            data = [stats.completed || 0, stats.pending || 0, stats.cancelled || 0];
            backgroundColor = [
                'rgba(34, 197, 94, 0.8)',   // Bright Green - Completed
                'rgba(251, 191, 36, 0.8)',  // Yellow - Pending
                'rgba(239, 68, 68, 0.8)'    // Red - Cancelled
            ];
            borderColor = [
                'rgba(34, 197, 94, 1)',
                'rgba(251, 191, 36, 1)',
                'rgba(239, 68, 68, 1)'
            ];
        }
        
        const chartData = {
            labels: labels,
            datasets: [{
                data: data,
                backgroundColor: backgroundColor,
                borderColor: borderColor,
                borderWidth: 2
            }]
        };
        
        // Debug log to verify data structure
        console.log('Chart Data:', { labels, data, backgroundColor });
        
        return chartData;
    };    const doughnutOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    color: isDarkMode ? '#d1d5db' : '#374151',
                    padding: 15,
                    font: {
                        size: 12
                    }
                }
            },
            tooltip: {
                backgroundColor: isDarkMode ? 'rgba(31, 41, 55, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                titleColor: isDarkMode ? '#f3f4f6' : '#111827',
                bodyColor: isDarkMode ? '#d1d5db' : '#374151',
                borderColor: isDarkMode ? '#4b5563' : '#e5e7eb',
                borderWidth: 1
            }
        }
    };

    // Pagination helper functions
    const getTotalPages = (totalItems) => {
        return Math.ceil(totalItems / itemsPerPage);
    };

    // Pagination Component with improved handling for large page counts
    const PaginationControls = ({ currentPage, totalItems, onPageChange }) => {
        const totalPages = getTotalPages(totalItems);
        
        if (totalPages <= 1) return null;

        // Generate page numbers to display
        const getPageNumbers = () => {
            const pages = [];
            const maxPagesToShow = 7; // Show max 7 page buttons
            
            if (totalPages <= maxPagesToShow) {
                // Show all pages if total is small
                for (let i = 1; i <= totalPages; i++) {
                    pages.push(i);
                }
            } else {
                // Always show first page
                pages.push(1);
                
                let startPage = Math.max(2, currentPage - 2);
                let endPage = Math.min(totalPages - 1, currentPage + 2);
                
                // Adjust if we're near the beginning
                if (currentPage <= 3) {
                    endPage = 5;
                }
                
                // Adjust if we're near the end
                if (currentPage >= totalPages - 2) {
                    startPage = totalPages - 4;
                }
                
                // Add ellipsis after first page if needed
                if (startPage > 2) {
                    pages.push('...');
                }
                
                // Add middle pages
                for (let i = startPage; i <= endPage; i++) {
                    pages.push(i);
                }
                
                // Add ellipsis before last page if needed
                if (endPage < totalPages - 1) {
                    pages.push('...');
                }
                
                // Always show last page
                pages.push(totalPages);
            }
            
            return pages;
        };

        const pageNumbers = getPageNumbers();

        return (
            <div className={`px-4 sm:px-6 py-4 border-t ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
                {/* Mobile Layout */}
                <div className="flex flex-col gap-3 sm:hidden">
                    <div className={`text-xs text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} results
                    </div>
                    <div className="flex items-center justify-between gap-2">
                        <button
                            onClick={() => onPageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                currentPage === 1
                                    ? isDarkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                    : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                            }`}
                        >
                            ‹ Previous
                        </button>
                        <div className={`px-3 py-2 rounded-md text-sm font-medium ${isDarkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'}`}>
                            {currentPage} / {totalPages}
                        </div>
                        <button
                            onClick={() => onPageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                currentPage === totalPages
                                    ? isDarkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                    : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                            }`}
                        >
                            Next ›
                        </button>
                    </div>
                </div>

                {/* Desktop Layout */}
                <div className="hidden sm:flex items-center justify-between">
                    <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-700'}`}>
                        Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} results
                    </div>
                    <div className="flex gap-1 items-center">
                        <button
                            onClick={() => onPageChange(1)}
                            disabled={currentPage === 1}
                            className={`hidden md:block px-2 py-1 rounded-md text-sm font-medium transition-colors ${
                                currentPage === 1
                                    ? isDarkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                    : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                            }`}
                            title="First page"
                        >
                            ««
                        </button>
                        <button
                            onClick={() => onPageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                                currentPage === 1
                                    ? isDarkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                    : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                            }`}
                        >
                            <span className="hidden md:inline">Previous</span>
                            <span className="md:hidden">‹</span>
                        </button>
                        {pageNumbers.map((page, index) => (
                            page === '...' ? (
                                <span key={`ellipsis-${index}`} className={`px-2 py-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                    ...
                                </span>
                            ) : (
                                <button
                                    key={page}
                                    onClick={() => onPageChange(page)}
                                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                                        currentPage === page
                                            ? isDarkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
                                            : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                                    }`}
                                >
                                    {page}
                                </button>
                            )
                        ))}
                        <button
                            onClick={() => onPageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                                currentPage === totalPages
                                    ? isDarkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                    : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                            }`}
                        >
                            <span className="hidden md:inline">Next</span>
                            <span className="md:hidden">›</span>
                        </button>
                        <button
                            onClick={() => onPageChange(totalPages)}
                            disabled={currentPage === totalPages}
                            className={`hidden md:block px-2 py-1 rounded-md text-sm font-medium transition-colors ${
                                currentPage === totalPages
                                    ? isDarkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                    : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                            }`}
                            title="Last page"
                        >
                            »»
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                    <p className={`mt-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading Dashboard...</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} p-6`}>
            {/* Header */}
            <div className="mb-8">
                <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Dashboard Overview
                </h1>
                <p className={`mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Welcome back, {user.user.name.split(' ')[0]}! Here's what's happening today.
                </p>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {/* Delivery Orders Card */}
                {hasDeliveryOrderAccess && (
                <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border p-6 hover:shadow-xl transition-all duration-300`}>
                    <div className="flex items-center justify-between mb-4">
                        <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
                            <FaTruck className={`text-2xl ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                        </div>
                        <span className={`text-xs font-semibold px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                            This Month
                        </span>
                    </div>
                    <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {deliveryOrderStats.total}
                    </h3>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
                        Delivery Orders
                    </p>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="text-center">
                            <div className="text-emerald-500 font-semibold">{deliveryOrderStats.completed}</div>
                            <div className="text-emerald-400 text-[10px] font-medium">
                                {deliveryOrderStats.total > 0 ? `${((deliveryOrderStats.completed / deliveryOrderStats.total) * 100).toFixed(1)}%` : '0%'}
                            </div>
                            <div className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>Completed</div>
                        </div>
                        <div className="text-center">
                            <div className="text-amber-500 font-semibold">{deliveryOrderStats.pending}</div>
                            <div className="text-amber-400 text-[10px] font-medium">
                                {deliveryOrderStats.total > 0 ? `${((deliveryOrderStats.pending / deliveryOrderStats.total) * 100).toFixed(1)}%` : '0%'}
                            </div>
                            <div className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>Pending</div>
                        </div>
                        <div className="text-center">
                            <div className="text-red-500 font-semibold">{deliveryOrderStats.cancelled}</div>
                            <div className="text-red-400 text-[10px] font-medium">
                                {deliveryOrderStats.total > 0 ? `${((deliveryOrderStats.cancelled / deliveryOrderStats.total) * 100).toFixed(1)}%` : '0%'}
                            </div>
                            <div className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>Cancelled</div>
                        </div>
                    </div>
                </div>
                )}

                {/* Quotations New Card */}
                {hasQuotationNewAccess && (
                <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border p-6 hover:shadow-xl transition-all duration-300`}>
                    <div className="flex items-center justify-between mb-4">
                        <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-purple-900/30' : 'bg-purple-100'}`}>
                            <FaFileAlt className={`text-2xl ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                        </div>
                        <span className={`text-xs font-semibold px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                            This Month
                        </span>
                    </div>
                    <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {quotationStats.total}
                    </h3>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
                        Quotations
                    </p>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="text-center">
                            <div className="text-amber-500 font-semibold">{quotationStats.pending}</div>
                            <div className="text-amber-400 text-[10px] font-medium">
                                {quotationStats.total > 0 ? `${((quotationStats.pending / quotationStats.total) * 100).toFixed(1)}%` : '0%'}
                            </div>
                            <div className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>Pending</div>
                        </div>
                        <div className="text-center">
                            <div className="text-emerald-500 font-semibold">{quotationStats.completed}</div>
                            <div className="text-emerald-400 text-[10px] font-medium">
                                {quotationStats.total > 0 ? `${((quotationStats.completed / quotationStats.total) * 100).toFixed(1)}%` : '0%'}
                            </div>
                            <div className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>Completed</div>
                        </div>
                        <div className="text-center">
                            <div className="text-blue-500 font-semibold">{quotationStats.partialComplete}</div>
                            <div className="text-blue-400 text-[10px] font-medium">
                                {quotationStats.total > 0 ? `${((quotationStats.partialComplete / quotationStats.total) * 100).toFixed(1)}%` : '0%'}
                            </div>
                            <div className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>Partial</div>
                        </div>
                        <div className="text-center">
                            <div className="text-purple-500 font-semibold">{quotationStats.refFollow}</div>
                            <div className="text-purple-400 text-[10px] font-medium">
                                {quotationStats.total > 0 ? `${((quotationStats.refFollow / quotationStats.total) * 100).toFixed(1)}%` : '0%'}
                            </div>
                            <div className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>Ref Follow</div>
                        </div>
                        <div className="text-center col-span-2">
                            <div className="text-red-500 font-semibold">{quotationStats.cancelled}</div>
                            <div className="text-red-400 text-[10px] font-medium">
                                {quotationStats.total > 0 ? `${((quotationStats.cancelled / quotationStats.total) * 100).toFixed(1)}%` : '0%'}
                            </div>
                            <div className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>Cancelled</div>
                        </div>
                    </div>
                </div>
                )}

                {/* Tally Quotations Card */}
                {hasTallyQuotationAccess && (
                <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border p-6 hover:shadow-xl transition-all duration-300`}>
                    <div className="flex items-center justify-between mb-4">
                        <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-green-900/30' : 'bg-green-100'}`}>
                            <FaFileInvoice className={`text-2xl ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
                        </div>
                        <span className={`text-xs font-semibold px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                            This Month
                        </span>
                    </div>
                    <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {tallyQuotationStats.total}
                    </h3>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
                        Tally Quotations
                    </p>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="text-center">
                            <div className="text-amber-500 font-semibold">{tallyQuotationStats.pending}</div>
                            <div className="text-amber-400 text-[10px] font-medium">
                                {tallyQuotationStats.total > 0 ? `${((tallyQuotationStats.pending / tallyQuotationStats.total) * 100).toFixed(1)}%` : '0%'}
                            </div>
                            <div className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>Pending</div>
                        </div>
                        <div className="text-center">
                            <div className="text-emerald-500 font-semibold">{tallyQuotationStats.completed}</div>
                            <div className="text-emerald-400 text-[10px] font-medium">
                                {tallyQuotationStats.total > 0 ? `${((tallyQuotationStats.completed / tallyQuotationStats.total) * 100).toFixed(1)}%` : '0%'}
                            </div>
                            <div className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>Completed</div>
                        </div>
                        <div className="text-center">
                            <div className="text-blue-500 font-semibold">{tallyQuotationStats.partialComplete}</div>
                            <div className="text-blue-400 text-[10px] font-medium">
                                {tallyQuotationStats.total > 0 ? `${((tallyQuotationStats.partialComplete / tallyQuotationStats.total) * 100).toFixed(1)}%` : '0%'}
                            </div>
                            <div className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>Partial</div>
                        </div>
                        <div className="text-center">
                            <div className="text-purple-500 font-semibold">{tallyQuotationStats.refFollow}</div>
                            <div className="text-purple-400 text-[10px] font-medium">
                                {tallyQuotationStats.total > 0 ? `${((tallyQuotationStats.refFollow / tallyQuotationStats.total) * 100).toFixed(1)}%` : '0%'}
                            </div>
                            <div className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>Ref Follow</div>
                        </div>
                        <div className="text-center col-span-2">
                            <div className="text-red-500 font-semibold">{tallyQuotationStats.cancelled}</div>
                            <div className="text-red-400 text-[10px] font-medium">
                                {tallyQuotationStats.total > 0 ? `${((tallyQuotationStats.cancelled / tallyQuotationStats.total) * 100).toFixed(1)}%` : '0%'}
                            </div>
                            <div className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>Cancelled</div>
                        </div>
                    </div>
                </div>
                )}
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Delivery Orders Trend */}
                {hasDeliveryOrderAccess && (
                <>
                <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border p-6`}>
                    <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Delivery Orders Trend (Last 7 Days)
                    </h3>
                    <div style={{ height: '250px' }}>
                        <Line 
                            data={getLineChartData(deliveryOrderStats.weeklyData || [], 'Delivery Orders')} 
                            options={chartOptions} 
                        />
                    </div>
                </div>

                {/* Delivery Orders Status Distribution */}
                <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border p-6`}>
                    <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Delivery Orders Status Distribution
                    </h3>
                    <div style={{ height: '250px' }}>
                        <Doughnut 
                            data={getDoughnutChartData(deliveryOrderStats, ['Completed', 'Pending', 'Cancelled'])} 
                            options={doughnutOptions} 
                        />
                    </div>
                </div>
                </>
                )}

                {/* Quotations New Trend */}
                {hasQuotationNewAccess && (
                <>
                <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border p-6`}>
                    <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Quotations (New) Trend (Last 7 Days)
                    </h3>
                    <div style={{ height: '250px' }}>
                        <Line 
                            data={getLineChartData(quotationStats.weeklyData || [], 'Quotations New')} 
                            options={chartOptions} 
                        />
                    </div>
                </div>

                {/* Quotations New Status Distribution */}
                <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border p-6`}>
                    <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Quotations (New) Status Distribution
                    </h3>
                    <div style={{ height: '250px' }}>
                        <Doughnut 
                            data={getDoughnutChartData(quotationStats, ['Pending', 'Completed', 'Partial Complete', 'Ref Follow', 'Cancelled'])} 
                            options={doughnutOptions} 
                        />
                    </div>
                </div>
                </>
                )}

                {/* Tally Quotations Trend */}
                {hasTallyQuotationAccess && (
                <>
                <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border p-6`}>
                    <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Tally Quotations Trend (Last 7 Days)
                    </h3>
                    <div style={{ height: '250px' }}>
                        <Line 
                            data={getLineChartData(tallyQuotationStats.weeklyData || [], 'Tally Quotations')} 
                            options={chartOptions} 
                        />
                    </div>
                </div>

                {/* Tally Quotations Status Distribution */}
                <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border p-6`}>
                    <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Tally Quotations Status Distribution
                    </h3>
                    <div style={{ height: '250px' }}>
                        <Doughnut 
                            data={getDoughnutChartData(tallyQuotationStats, ['Pending', 'Completed', 'Partial Complete', 'Ref Follow', 'Cancelled'])} 
                            options={doughnutOptions} 
                        />
                    </div>
                </div>
                </>
                )}
            </div>

            {/* Tally Items Management Section */}
            {hasTallyItemsAccess && (
            <div className="mb-8">
                <h2 className={`text-2xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    📦 Tally Items Management
                </h2>
                
                {/* First Row - Total Items and Bar Chart */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    {/* Total Items Card */}
                    <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border p-6`}>
                        <div className="flex items-center justify-between mb-4">
                            <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-purple-900/30' : 'bg-purple-100'}`}>
                                <FaFileInvoice className={`text-2xl ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                            </div>
                        </div>
                        <h3 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {tallyItemsStats.total}
                        </h3>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Total Tally Items
                        </p>
                    </div>

                    {/* Items Created - Last 7 Days Bar Chart */}
                    <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border p-6`}>
                        <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            Tally Items Created (Last 7 Days)
                        </h3>
                        <div style={{ height: '250px' }}>
                            <Bar 
                                data={{
                                    labels: tallyItemsStats.last7Days?.map(d => d.label) || [],
                                    datasets: [{
                                        label: 'Items Created',
                                        data: tallyItemsStats.last7Days?.map(d => d.count) || [],
                                        backgroundColor: isDarkMode ? 'rgba(168, 85, 247, 0.8)' : 'rgba(147, 51, 234, 0.8)',
                                        borderColor: isDarkMode ? 'rgb(168, 85, 247)' : 'rgb(147, 51, 234)',
                                        borderWidth: 2
                                    }]
                                }}
                                options={chartOptions} 
                            />
                        </div>
                    </div>
                </div>

                {/* Second Row - Three Assignment Pie Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Brand Assignment Pie Chart */}
                    <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border p-6`}>
                        <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            Brand Assignment
                        </h3>
                        <div style={{ height: '250px' }}>
                            <Pie 
                                data={{
                                    labels: ['With Brand', 'Without Brand'],
                                    datasets: [{
                                        data: [
                                            tallyItemsStats.withBrands,
                                            tallyItemsStats.total - tallyItemsStats.withBrands
                                        ],
                                        backgroundColor: [
                                            isDarkMode ? 'rgba(59, 130, 246, 0.8)' : 'rgba(37, 99, 235, 0.8)',
                                            isDarkMode ? 'rgba(107, 114, 128, 0.3)' : 'rgba(209, 213, 219, 0.5)'
                                        ],
                                        borderColor: [
                                            isDarkMode ? 'rgb(59, 130, 246)' : 'rgb(37, 99, 235)',
                                            isDarkMode ? 'rgb(107, 114, 128)' : 'rgb(209, 213, 219)'
                                        ],
                                        borderWidth: 2
                                    }]
                                }}
                                options={doughnutOptions} 
                            />
                        </div>
                        <div className="mt-4 text-center">
                            <p className={`text-2xl font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                {tallyItemsStats.total > 0 
                                    ? ((tallyItemsStats.withBrands / tallyItemsStats.total) * 100).toFixed(1) 
                                    : '0'}%
                            </p>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                Items with Brand Assigned
                            </p>
                        </div>
                    </div>

                    {/* Parameter Assignment Pie Chart */}
                    <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border p-6`}>
                        <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            Parameter Assignment
                        </h3>
                        <div style={{ height: '250px' }}>
                            <Pie 
                                data={{
                                    labels: ['With Parameter', 'Without Parameter'],
                                    datasets: [{
                                        data: [
                                            tallyItemsStats.withParameters,
                                            tallyItemsStats.total - tallyItemsStats.withParameters
                                        ],
                                        backgroundColor: [
                                            isDarkMode ? 'rgba(34, 197, 94, 0.8)' : 'rgba(22, 163, 74, 0.8)',
                                            isDarkMode ? 'rgba(107, 114, 128, 0.3)' : 'rgba(209, 213, 219, 0.5)'
                                        ],
                                        borderColor: [
                                            isDarkMode ? 'rgb(34, 197, 94)' : 'rgb(22, 163, 74)',
                                            isDarkMode ? 'rgb(107, 114, 128)' : 'rgb(209, 213, 219)'
                                        ],
                                        borderWidth: 2
                                    }]
                                }}
                                options={doughnutOptions} 
                            />
                        </div>
                        <div className="mt-4 text-center">
                            <p className={`text-2xl font-bold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                                {tallyItemsStats.total > 0 
                                    ? ((tallyItemsStats.withParameters / tallyItemsStats.total) * 100).toFixed(1) 
                                    : '0'}%
                            </p>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                Items with Parameter Assigned
                            </p>
                        </div>
                    </div>

                    {/* Image Assignment Pie Chart */}
                    <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border p-6`}>
                        <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            Image Assignment
                        </h3>
                        <div style={{ height: '250px' }}>
                            <Pie 
                                data={{
                                    labels: ['With Images', 'Without Images'],
                                    datasets: [{
                                        data: [
                                            tallyItemsStats.withImages,
                                            tallyItemsStats.total - tallyItemsStats.withImages
                                        ],
                                        backgroundColor: [
                                            isDarkMode ? 'rgba(168, 85, 247, 0.8)' : 'rgba(147, 51, 234, 0.8)',
                                            isDarkMode ? 'rgba(107, 114, 128, 0.3)' : 'rgba(209, 213, 219, 0.5)'
                                        ],
                                        borderColor: [
                                            isDarkMode ? 'rgb(168, 85, 247)' : 'rgb(147, 51, 234)',
                                            isDarkMode ? 'rgb(107, 114, 128)' : 'rgb(209, 213, 219)'
                                        ],
                                        borderWidth: 2
                                    }]
                                }}
                                options={doughnutOptions} 
                            />
                        </div>
                        <div className="mt-4 text-center">
                            <p className={`text-2xl font-bold ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                                {tallyItemsStats.total > 0 
                                    ? ((tallyItemsStats.withImages / tallyItemsStats.total) * 100).toFixed(1) 
                                    : '0'}%
                            </p>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                Items with Images Assigned
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            )}

            {/* Tally Ledger Management Section */}
            {hasTallyLedgerAccess && (
            <div className="mb-8">
                <h2 className={`text-2xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    📊 Tally Ledger Management
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Total Ledgers Card */}
                    <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border p-6`}>
                        <div className="flex items-center justify-between mb-4">
                            <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-green-900/30' : 'bg-green-100'}`}>
                                <FaChartLine className={`text-2xl ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
                            </div>
                        </div>
                        <h3 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {tallyLedgerStats.total}
                        </h3>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Total Ledger Entries
                        </p>
                    </div>

                    {/* Ledgers Created - Last 7 Days Bar Chart */}
                    <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border p-6`}>
                        <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            Ledger Entries Created (Last 7 Days)
                        </h3>
                        <div style={{ height: '250px' }}>
                            <Bar 
                                data={{
                                    labels: tallyLedgerStats.last7Days?.map(d => d.label) || [],
                                    datasets: [{
                                        label: 'Ledgers Created',
                                        data: tallyLedgerStats.last7Days?.map(d => d.count) || [],
                                        backgroundColor: isDarkMode ? 'rgba(34, 197, 94, 0.8)' : 'rgba(22, 163, 74, 0.8)',
                                        borderColor: isDarkMode ? 'rgb(34, 197, 94)' : 'rgb(22, 163, 74)',
                                        borderWidth: 2
                                    }]
                                }}
                                options={chartOptions} 
                            />
                        </div>
                    </div>
                </div>
            </div>
            )}

            {/* New Pie Charts Section - 6 Charts: 3 for Created By Employee & 3 for Salesman Distribution */}
            
            {/* Records by Type - Created By Employee Distribution (3 Charts) */}
            <div className="mb-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Delivery Orders Created By */}
                    {hasDeliveryOrderAccess && (
                        <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border p-6`}>
                            <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                Delivery Orders (Created By)
                            </h3>
                            <div style={{ height: '250px' }}>
                                <Pie 
                                    data={(() => {
                                        const chartData = deliveryOrdersChartData.createdBy || [];
                                        
                                        const employeeNames = chartData.map(item => item.name);
                                        const employeeCounts = chartData.map(item => item.count);
                                        
                                        const colors = [
                                            'rgba(59, 130, 246, 0.8)', 'rgba(168, 85, 247, 0.8)', 'rgba(34, 197, 94, 0.8)',
                                            'rgba(249, 115, 22, 0.8)', 'rgba(239, 68, 68, 0.8)', 'rgba(236, 72, 153, 0.8)',
                                            'rgba(14, 165, 233, 0.8)', 'rgba(132, 204, 22, 0.8)', 'rgba(251, 191, 36, 0.8)',
                                            'rgba(99, 102, 241, 0.8)'
                                        ];
                                        
                                        const borderColors = colors.map(color => color.replace('0.8', '1'));
                                        
                                        return {
                                            labels: employeeNames.length > 0 ? employeeNames : ['No Data'],
                                            datasets: [{
                                                data: employeeCounts.length > 0 ? employeeCounts : [1],
                                                backgroundColor: colors.slice(0, employeeNames.length || 1),
                                                borderColor: borderColors.slice(0, employeeNames.length || 1),
                                                borderWidth: 2
                                            }]
                                        };
                                    })()}
                                    options={{
                                        ...doughnutOptions,
                                        plugins: {
                                            ...doughnutOptions.plugins,
                                            legend: {
                                                ...doughnutOptions.plugins.legend,
                                                position: 'bottom',
                                                labels: {
                                                    ...doughnutOptions.plugins.legend.labels,
                                                    padding: 10,
                                                    font: {
                                                        size: 11,
                                                        weight: 600
                                                    }
                                                }
                                            }
                                        }
                                    }} 
                                />
                            </div>
                        </div>
                    )}

                    {/* Quotation New Created By */}
                    {hasQuotationNewAccess && (
                        <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border p-6`}>
                            <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                Quotation New (Created By)
                            </h3>
                            <div style={{ height: '250px' }}>
                                <Pie 
                                    data={(() => {
                                        const chartData = quotationsChartData.createdBy || [];
                                        
                                        const employeeNames = chartData.map(item => item.name);
                                        const employeeCounts = chartData.map(item => item.count);
                                        
                                        const colors = [
                                            'rgba(168, 85, 247, 0.8)', 'rgba(59, 130, 246, 0.8)', 'rgba(34, 197, 94, 0.8)',
                                            'rgba(249, 115, 22, 0.8)', 'rgba(239, 68, 68, 0.8)', 'rgba(236, 72, 153, 0.8)',
                                            'rgba(14, 165, 233, 0.8)', 'rgba(132, 204, 22, 0.8)', 'rgba(251, 191, 36, 0.8)',
                                            'rgba(99, 102, 241, 0.8)'
                                        ];
                                        
                                        const borderColors = colors.map(color => color.replace('0.8', '1'));
                                        
                                        return {
                                            labels: employeeNames.length > 0 ? employeeNames : ['No Data'],
                                            datasets: [{
                                                data: employeeCounts.length > 0 ? employeeCounts : [1],
                                                backgroundColor: colors.slice(0, employeeNames.length || 1),
                                                borderColor: borderColors.slice(0, employeeNames.length || 1),
                                                borderWidth: 2
                                            }]
                                        };
                                    })()}
                                    options={{
                                        ...doughnutOptions,
                                        plugins: {
                                            ...doughnutOptions.plugins,
                                            legend: {
                                                ...doughnutOptions.plugins.legend,
                                                position: 'bottom',
                                                labels: {
                                                    ...doughnutOptions.plugins.legend.labels,
                                                    padding: 10,
                                                    font: {
                                                        size: 11,
                                                        weight: 600
                                                    }
                                                }
                                            }
                                        }
                                    }} 
                                />
                            </div>
                        </div>
                    )}

                    {/* Tally Quotations Created By */}
                    {hasTallyQuotationAccess && (
                        <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border p-6`}>
                            <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                Tally Quotations (Created By)
                            </h3>
                            <div style={{ height: '250px' }}>
                                <Pie 
                                    data={(() => {
                                        const chartData = tallyQuotationsChartData.createdBy || [];
                                        
                                        const employeeNames = chartData.map(item => item.name);
                                        const employeeCounts = chartData.map(item => item.count);
                                        
                                        const colors = [
                                            'rgba(34, 197, 94, 0.8)', 'rgba(59, 130, 246, 0.8)', 'rgba(168, 85, 247, 0.8)',
                                            'rgba(249, 115, 22, 0.8)', 'rgba(239, 68, 68, 0.8)', 'rgba(236, 72, 153, 0.8)',
                                            'rgba(14, 165, 233, 0.8)', 'rgba(132, 204, 22, 0.8)', 'rgba(251, 191, 36, 0.8)',
                                            'rgba(99, 102, 241, 0.8)'
                                        ];
                                        
                                        const borderColors = colors.map(color => color.replace('0.8', '1'));
                                        
                                        return {
                                            labels: employeeNames.length > 0 ? employeeNames : ['No Data'],
                                            datasets: [{
                                                data: employeeCounts.length > 0 ? employeeCounts : [1],
                                                backgroundColor: colors.slice(0, employeeNames.length || 1),
                                                borderColor: borderColors.slice(0, employeeNames.length || 1),
                                                borderWidth: 2
                                            }]
                                        };
                                    })()}
                                    options={{
                                        ...doughnutOptions,
                                        plugins: {
                                            ...doughnutOptions.plugins,
                                            legend: {
                                                ...doughnutOptions.plugins.legend,
                                                position: 'bottom',
                                                labels: {
                                                    ...doughnutOptions.plugins.legend.labels,
                                                    padding: 10,
                                                    font: {
                                                        size: 11,
                                                        weight: 600
                                                    }
                                                }
                                            }
                                        }
                                    }} 
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Records by Salesman - Distribution per Type (3 Charts) */}
            <div className="mb-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Delivery Orders by Salesman */}
                    {hasDeliveryOrderAccess && (
                        <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border p-6`}>
                            <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                Delivery Orders (Salesman Distribution)
                            </h3>
                            <div style={{ height: '250px' }}>
                                <Pie 
                                    data={(() => {
                                        const chartData = deliveryOrdersChartData.salesman || [];
                                        
                                        const salesmanNames = chartData.map(item => item.name);
                                        const salesmanCounts = chartData.map(item => item.count);
                                        
                                        const colors = [
                                            'rgba(59, 130, 246, 0.8)', 'rgba(168, 85, 247, 0.8)', 'rgba(34, 197, 94, 0.8)',
                                            'rgba(249, 115, 22, 0.8)', 'rgba(239, 68, 68, 0.8)', 'rgba(236, 72, 153, 0.8)',
                                            'rgba(14, 165, 233, 0.8)', 'rgba(132, 204, 22, 0.8)', 'rgba(251, 191, 36, 0.8)',
                                            'rgba(99, 102, 241, 0.8)'
                                        ];
                                        
                                        const borderColors = colors.map(color => color.replace('0.8', '1'));
                                        
                                        return {
                                            labels: salesmanNames.length > 0 ? salesmanNames : ['No Data'],
                                            datasets: [{
                                                data: salesmanCounts.length > 0 ? salesmanCounts : [1],
                                                backgroundColor: colors.slice(0, salesmanNames.length || 1),
                                                borderColor: borderColors.slice(0, salesmanNames.length || 1),
                                                borderWidth: 2
                                            }]
                                        };
                                    })()}
                                    options={{
                                        ...doughnutOptions,
                                        plugins: {
                                            ...doughnutOptions.plugins,
                                            legend: {
                                                ...doughnutOptions.plugins.legend,
                                                position: 'bottom',
                                                labels: {
                                                    ...doughnutOptions.plugins.legend.labels,
                                                    padding: 8,
                                                    font: {
                                                        size: 10,
                                                        weight: 600
                                                    }
                                                }
                                            }
                                        }
                                    }} 
                                />
                            </div>
                        </div>
                    )}

                    {/* Quotation New by Salesman */}
                    {hasQuotationNewAccess && (
                        <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border p-6`}>
                            <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                Quotation New (Salesman Distribution)
                            </h3>
                            <div style={{ height: '250px' }}>
                                <Pie 
                                    data={(() => {
                                        const chartData = quotationsChartData.salesman || [];
                                        
                                        const salesmanNames = chartData.map(item => item.name);
                                        const salesmanCounts = chartData.map(item => item.count);
                                        
                                        const colors = [
                                            'rgba(168, 85, 247, 0.8)', 'rgba(59, 130, 246, 0.8)', 'rgba(34, 197, 94, 0.8)',
                                            'rgba(249, 115, 22, 0.8)', 'rgba(239, 68, 68, 0.8)', 'rgba(236, 72, 153, 0.8)',
                                            'rgba(14, 165, 233, 0.8)', 'rgba(132, 204, 22, 0.8)', 'rgba(251, 191, 36, 0.8)',
                                            'rgba(99, 102, 241, 0.8)'
                                        ];
                                        
                                        const borderColors = colors.map(color => color.replace('0.8', '1'));
                                        
                                        return {
                                            labels: salesmanNames.length > 0 ? salesmanNames : ['No Data'],
                                            datasets: [{
                                                data: salesmanCounts.length > 0 ? salesmanCounts : [1],
                                                backgroundColor: colors.slice(0, salesmanNames.length || 1),
                                                borderColor: borderColors.slice(0, salesmanNames.length || 1),
                                                borderWidth: 2
                                            }]
                                        };
                                    })()}
                                    options={{
                                        ...doughnutOptions,
                                        plugins: {
                                            ...doughnutOptions.plugins,
                                            legend: {
                                                ...doughnutOptions.plugins.legend,
                                                position: 'bottom',
                                                labels: {
                                                    ...doughnutOptions.plugins.legend.labels,
                                                    padding: 8,
                                                    font: {
                                                        size: 10,
                                                        weight: 600
                                                    }
                                                }
                                            }
                                        }
                                    }} 
                                />
                            </div>
                        </div>
                    )}

                    {/* Tally Quotations by Salesman */}
                    {hasTallyQuotationAccess && (
                        <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border p-6`}>
                            <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                Tally Quotations (Salesman Distribution)
                            </h3>
                            <div style={{ height: '250px' }}>
                                <Pie 
                                    data={(() => {
                                        const chartData = tallyQuotationsChartData.salesman || [];
                                        
                                        const salesmanNames = chartData.map(item => item.name);
                                        const salesmanCounts = chartData.map(item => item.count);
                                        
                                        const colors = [
                                            'rgba(34, 197, 94, 0.8)', 'rgba(59, 130, 246, 0.8)', 'rgba(168, 85, 247, 0.8)',
                                            'rgba(249, 115, 22, 0.8)', 'rgba(239, 68, 68, 0.8)', 'rgba(236, 72, 153, 0.8)',
                                            'rgba(14, 165, 233, 0.8)', 'rgba(132, 204, 22, 0.8)', 'rgba(251, 191, 36, 0.8)',
                                            'rgba(99, 102, 241, 0.8)'
                                        ];
                                        
                                        const borderColors = colors.map(color => color.replace('0.8', '1'));
                                        
                                        return {
                                            labels: salesmanNames.length > 0 ? salesmanNames : ['No Data'],
                                            datasets: [{
                                                data: salesmanCounts.length > 0 ? salesmanCounts : [1],
                                                backgroundColor: colors.slice(0, salesmanNames.length || 1),
                                                borderColor: borderColors.slice(0, salesmanNames.length || 1),
                                                borderWidth: 2
                                            }]
                                        };
                                    })()}
                                    options={{
                                        ...doughnutOptions,
                                        plugins: {
                                            ...doughnutOptions.plugins,
                                            legend: {
                                                ...doughnutOptions.plugins.legend,
                                                position: 'bottom',
                                                labels: {
                                                    ...doughnutOptions.plugins.legend.labels,
                                                    padding: 8,
                                                    font: {
                                                        size: 10,
                                                        weight: 600
                                                    }
                                                }
                                            }
                                        }
                                    }} 
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Recent Records Section */}
            <div className="space-y-6">
                {/* Recent Delivery Orders */}
                {hasDeliveryOrderAccess && (
                <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border overflow-hidden`}>
                    <div className="p-4 sm:p-6 border-b border-gray-700">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
                            <div>
                                <h3 className={`text-base sm:text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                    Recent Delivery Orders
                                </h3>
                                <span className={`text-xs sm:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                    Total: {deliveryOrderTotal}
                                </span>
                            </div>
                            {/* Search Input with Apply Button */}
                            <div className="w-full sm:w-auto flex gap-2">
                                <div className="relative flex-1 sm:min-w-[280px]">
                                    <input
                                        type="text"
                                        placeholder="Search DO, recipient, creator, salesman..."
                                        value={deliveryOrderSearchTemp}
                                        onChange={(e) => setDeliveryOrderSearchTemp(e.target.value)}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                handleApplyDeliveryOrderFilters();
                                            }
                                        }}
                                        className={`w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                            isDarkMode 
                                                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                                                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                        }`}
                                    />
                                    <svg className={`absolute left-2.5 top-2.5 w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                </div>
                                <button
                                    onClick={handleApplyDeliveryOrderFilters}
                                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                                        isDarkMode 
                                            ? 'bg-blue-600 text-white hover:bg-blue-700' 
                                            : 'bg-blue-600 text-white hover:bg-blue-700'
                                    }`}
                                >
                                    Apply
                                </button>
                                {deliveryOrderSearch && (
                                    <button
                                        onClick={handleClearDeliveryOrderFilters}
                                        className={`px-3 py-2 text-sm rounded-lg transition-colors whitespace-nowrap ${
                                            isDarkMode 
                                                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                        }`}
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    {/* Mobile Card View */}
                    <div className="block md:hidden">
                        {deliveryOrders.length === 0 ? (
                            <div className={`p-8 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                No delivery orders found
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-700">
                                {deliveryOrders.map((order) => (
                                    <div key={order._id} className={`p-4 ${isDarkMode ? 'hover:bg-gray-750' : 'hover:bg-gray-50'} transition-colors`}>
                                        <div className="flex justify-between items-start mb-3">
                                            <div>
                                                <div className={`text-sm font-semibold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                                    {order.doNumber}
                                                </div>
                                                <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                                    {new Date(order.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} • {new Date(order.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                                                </div>
                                            </div>
                                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                                order.status === 'Completed' 
                                                    ? 'bg-green-100 text-green-800' 
                                                    : order.status === 'Pending' 
                                                    ? 'bg-yellow-100 text-yellow-800' 
                                                    : 'bg-red-100 text-red-800'
                                            }`}>
                                                {order.status}
                                            </span>
                                        </div>
                                        <div className="space-y-2 mb-3">
                                            <div className="flex justify-between text-sm">
                                                <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Recipient:</span>
                                                <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'} truncate ml-2`}>
                                                    {order.recipient?.name || order.recipient?.businessName || 'N/A'}
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Created By:</span>
                                                <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                                                    {order.createdBy?.name || 'N/A'}
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Amount:</span>
                                                <span className={`font-semibold ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                                                    {formatCurrency(order.totalAmountWithGST || order.totalAmountWithoutGST || 0)}
                                                </span>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleViewRecord(order, 'delivery')}
                                            className={`w-full px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                                isDarkMode 
                                                    ? 'bg-indigo-600 hover:bg-indigo-700 text-white' 
                                                    : 'bg-indigo-500 hover:bg-indigo-600 text-white'
                                            }`}
                                        >
                                            View Details
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Desktop Table View */}
                    <div className="hidden md:block overflow-x-auto -mx-4 sm:mx-0">
                        <div className="inline-block min-w-full align-middle">
                            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className={isDarkMode ? 'bg-gray-750' : 'bg-gray-50'}>
                                        <tr>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                                                DO Number
                                            </th>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                                                Date
                                            </th>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                                                Recipient
                                            </th>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider hidden lg:table-cell`}>
                                                Created By
                                            </th>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                                                Total Amount
                                            </th>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                                                Status
                                            </th>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                            <tbody className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                                {deliveryOrders.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className={`px-3 sm:px-6 py-8 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                            No delivery orders found
                                        </td>
                                    </tr>
                                ) : (
                                    deliveryOrders.map((order) => (
                                        <tr key={order._id} className={`${isDarkMode ? 'hover:bg-gray-750' : 'hover:bg-gray-50'} transition-colors`}>
                                            <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                                {order.doNumber}
                                            </td>
                                            <td className={`px-3 sm:px-6 py-4 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                                                <div>{new Date(order.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                                                <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                                    {new Date(order.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                                                </div>
                                            </td>
                                            <td className={`px-3 sm:px-6 py-4 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                                                <div className="max-w-xs truncate">{order.recipient?.name || order.recipient?.businessName || 'N/A'}</div>
                                            </td>
                                            <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} hidden lg:table-cell`}>
                                                {order.createdBy?.name || 'N/A'}
                                            </td>
                                            <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-semibold ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                                                {formatCurrency(order.totalAmountWithGST || order.totalAmountWithoutGST || 0)}
                                            </td>
                                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                    order.status === 'Completed' 
                                                        ? 'bg-green-100 text-green-800' 
                                                        : order.status === 'Pending' 
                                                        ? 'bg-yellow-100 text-yellow-800' 
                                                        : 'bg-red-100 text-red-800'
                                                }`}>
                                                    {order.status}
                                                </span>
                                            </td>
                                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm">
                                                <button
                                                    onClick={() => handleViewRecord(order, 'delivery')}
                                                    className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                                                        isDarkMode 
                                                            ? 'bg-indigo-600 hover:bg-indigo-700 text-white' 
                                                            : 'bg-indigo-500 hover:bg-indigo-600 text-white'
                                                    }`}
                                                >
                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                            </div>
                        </div>
                    </div>
                    <PaginationControls 
                        currentPage={deliveryOrderPage}
                        totalItems={deliveryOrderTotal}
                        onPageChange={setDeliveryOrderPage}
                    />
                </div>
                )}

                {/* Recent Quotations */}
                {hasQuotationNewAccess && (
                <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border overflow-hidden`}>
                    <div className="p-4 sm:p-6 border-b border-gray-700">
                        <div className="flex flex-col gap-3 sm:gap-4">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                                <div>
                                    <h3 className={`text-base sm:text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                        Recent Quotations
                                    </h3>
                                    <span className={`text-xs sm:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                        Total: {quotationTotal}
                                    </span>
                                </div>
                            </div>
                            {/* Search and Date Range Filters */}
                            <div className="flex flex-col gap-2">
                                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                                    {/* Search Input */}
                                    <div className="flex-1 sm:min-w-[200px]">
                                        <div className="relative">
                                            <input
                                                type="text"
                                                placeholder="Search quotation, recipient, creator, salesman..."
                                                value={quotationSearchTemp}
                                                onChange={(e) => setQuotationSearchTemp(e.target.value)}
                                                onKeyPress={(e) => {
                                                    if (e.key === 'Enter') {
                                                        handleApplyQuotationFilters();
                                                    }
                                                }}
                                                className={`w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                                    isDarkMode 
                                                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                                                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                                }`}
                                            />
                                            <svg className={`absolute left-2.5 top-2.5 w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                            </svg>
                                        </div>
                                    </div>
                                    {/* Date Range */}
                                    <div className="flex flex-col sm:flex-row gap-2">
                                        <input
                                            type="date"
                                            placeholder="Start Date"
                                            value={quotationDateRangeTemp.startDate}
                                            onChange={(e) => setQuotationDateRangeTemp(prev => ({ ...prev, startDate: e.target.value }))}
                                            className={`px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                                isDarkMode 
                                                    ? 'bg-gray-700 border-gray-600 text-white' 
                                                    : 'bg-white border-gray-300 text-gray-900'
                                            }`}
                                        />
                                        <input
                                            type="date"
                                            placeholder="End Date"
                                            value={quotationDateRangeTemp.endDate}
                                            onChange={(e) => setQuotationDateRangeTemp(prev => ({ ...prev, endDate: e.target.value }))}
                                            className={`px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                                isDarkMode 
                                                    ? 'bg-gray-700 border-gray-600 text-white' 
                                                    : 'bg-white border-gray-300 text-gray-900'
                                            }`}
                                        />
                                    </div>
                                </div>
                                {/* Action Buttons */}
                                <div className="flex gap-2 justify-end">
                                    <button
                                        onClick={handleApplyQuotationFilters}
                                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                            isDarkMode 
                                                ? 'bg-blue-600 text-white hover:bg-blue-700' 
                                                : 'bg-blue-600 text-white hover:bg-blue-700'
                                        }`}
                                    >
                                        Apply Filters
                                    </button>
                                    {(quotationSearch || quotationDateRange.startDate || quotationDateRange.endDate) && (
                                        <button
                                            onClick={handleClearQuotationFilters}
                                            className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                                                isDarkMode 
                                                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                            }`}
                                        >
                                            Clear Filters
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Mobile Card View */}
                    <div className="block md:hidden">
                        {quotations.length === 0 ? (
                            <div className={`p-8 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                No quotations found
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-700">
                                {quotations.map((quote) => (
                                    <div key={quote._id} className={`p-4 ${isDarkMode ? 'hover:bg-gray-750' : 'hover:bg-gray-50'} transition-colors`}>
                                        <div className="flex justify-between items-start mb-3">
                                            <div>
                                                <div className={`text-sm font-semibold ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                                                    {quote.id || quote._id}
                                                </div>
                                                <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                                    {new Date(quote.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} • {new Date(quote.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                                                </div>
                                            </div>
                                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                                quote.status === 'Approved' 
                                                    ? 'bg-green-100 text-green-800' 
                                                    : quote.status === 'Pending' 
                                                    ? 'bg-yellow-100 text-yellow-800' 
                                                    : 'bg-red-100 text-red-800'
                                            }`}>
                                                {quote.status || 'Pending'}
                                            </span>
                                        </div>
                                        <div className="space-y-2 mb-3">
                                            <div className="flex justify-between text-sm">
                                                <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Recipient:</span>
                                                <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'} truncate ml-2`}>
                                                    {quote.recipient?.name || quote.recipient?.businessName || 'N/A'}
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Created By:</span>
                                                <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                                                    {quote.createdBy?.name || 'N/A'}
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Amount:</span>
                                                <span className={`font-semibold ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                                                    {formatCurrency(quote.totalValue || quote.totalAmount || 0)}
                                                </span>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleViewRecord(quote, 'quotation')}
                                            className={`w-full px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                                isDarkMode 
                                                    ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                                                    : 'bg-purple-500 hover:bg-purple-600 text-white'
                                            }`}
                                        >
                                            View Details
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Desktop Table View */}
                    <div className="hidden md:block overflow-x-auto -mx-4 sm:mx-0">
                        <div className="inline-block min-w-full align-middle">
                            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className={isDarkMode ? 'bg-gray-750' : 'bg-gray-50'}>
                                        <tr>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                                                Quotation ID
                                            </th>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                                                Date
                                            </th>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                                                Recipient
                                            </th>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider hidden lg:table-cell`}>
                                                Created By
                                            </th>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                                                Total Amount
                                            </th>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                                                Status
                                            </th>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                            <tbody className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                                {quotations.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className={`px-3 sm:px-6 py-8 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                            No quotations found
                                        </td>
                                    </tr>
                                ) : (
                                    quotations.map((quote) => (
                                        <tr key={quote._id} className={`${isDarkMode ? 'hover:bg-gray-750' : 'hover:bg-gray-50'} transition-colors`}>
                                            <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                                                {quote.id || quote._id}
                                            </td>
                                            <td className={`px-3 sm:px-6 py-4 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                                                <div>{new Date(quote.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                                                <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                                    {new Date(quote.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                                                </div>
                                            </td>
                                            <td className={`px-3 sm:px-6 py-4 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                                                <div className="max-w-xs truncate">{quote.recipient?.name || quote.recipient?.businessName || 'N/A'}</div>
                                            </td>
                                            <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} hidden lg:table-cell`}>
                                                {quote.createdBy?.name || 'N/A'}
                                            </td>
                                            <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-semibold ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                                                {formatCurrency(quote.totalValue || quote.totalAmount || 0)}
                                            </td>
                                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                    quote.status === 'Approved' 
                                                        ? 'bg-green-100 text-green-800' 
                                                        : quote.status === 'Pending' 
                                                        ? 'bg-yellow-100 text-yellow-800' 
                                                        : 'bg-red-100 text-red-800'
                                                }`}>
                                                    {quote.status || 'Pending'}
                                                </span>
                                            </td>
                                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm">
                                                <button
                                                    onClick={() => handleViewRecord(quote, 'quotation')}
                                                    className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                                                        isDarkMode 
                                                            ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                                                            : 'bg-purple-500 hover:bg-purple-600 text-white'
                                                    }`}
                                                >
                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                            </div>
                        </div>
                    </div>
                    <PaginationControls 
                        currentPage={quotationPage}
                        totalItems={quotationTotal}
                        onPageChange={setQuotationPage}
                    />
                </div>
                )}

                {/* Recent Tally Quotations */}
                {hasTallyQuotationAccess && (
                <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border overflow-hidden`}>
                    <div className="p-4 sm:p-6 border-b border-gray-700">
                        <div className="flex flex-col gap-3 sm:gap-4">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                                <div>
                                    <h3 className={`text-base sm:text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                        Recent Tally Quotations
                                    </h3>
                                    <span className={`text-xs sm:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                        Total: {tallyQuotationTotal}
                                    </span>
                                </div>
                            </div>
                            {/* Search and Date Range Filters */}
                            <div className="flex flex-col gap-2">
                                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                                    {/* Search Input */}
                                    <div className="flex-1 sm:min-w-[200px]">
                                        <div className="relative">
                                            <input
                                                type="text"
                                                placeholder="Search quotation, recipient, creator, salesman..."
                                                value={tallyQuotationSearchTemp}
                                                onChange={(e) => setTallyQuotationSearchTemp(e.target.value)}
                                                onKeyPress={(e) => {
                                                    if (e.key === 'Enter') {
                                                        handleApplyTallyQuotationFilters();
                                                    }
                                                }}
                                                className={`w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                                    isDarkMode 
                                                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                                                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                                }`}
                                            />
                                            <svg className={`absolute left-2.5 top-2.5 w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                            </svg>
                                        </div>
                                    </div>
                                    {/* Date Range */}
                                    <div className="flex flex-col sm:flex-row gap-2">
                                        <input
                                            type="date"
                                            placeholder="Start Date"
                                            value={tallyQuotationDateRangeTemp.startDate}
                                            onChange={(e) => setTallyQuotationDateRangeTemp(prev => ({ ...prev, startDate: e.target.value }))}
                                            className={`px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                                isDarkMode 
                                                    ? 'bg-gray-700 border-gray-600 text-white' 
                                                    : 'bg-white border-gray-300 text-gray-900'
                                            }`}
                                        />
                                        <input
                                            type="date"
                                            placeholder="End Date"
                                            value={tallyQuotationDateRangeTemp.endDate}
                                            onChange={(e) => setTallyQuotationDateRangeTemp(prev => ({ ...prev, endDate: e.target.value }))}
                                            className={`px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                                isDarkMode 
                                                    ? 'bg-gray-700 border-gray-600 text-white' 
                                                    : 'bg-white border-gray-300 text-gray-900'
                                            }`}
                                        />
                                    </div>
                                </div>
                                {/* Action Buttons */}
                                <div className="flex gap-2 justify-end">
                                    <button
                                        onClick={handleApplyTallyQuotationFilters}
                                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                            isDarkMode 
                                                ? 'bg-blue-600 text-white hover:bg-blue-700' 
                                                : 'bg-blue-600 text-white hover:bg-blue-700'
                                        }`}
                                    >
                                        Apply Filters
                                    </button>
                                    {(tallyQuotationSearch || tallyQuotationDateRange.startDate || tallyQuotationDateRange.endDate) && (
                                        <button
                                            onClick={handleClearTallyQuotationFilters}
                                            className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                                                isDarkMode 
                                                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                            }`}
                                        >
                                            Clear Filters
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Mobile Card View */}
                    <div className="block md:hidden">
                        {tallyQuotations.length === 0 ? (
                            <div className={`p-8 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                No tally quotations found
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-700">
                                {tallyQuotations.map((quote) => (
                                    <div key={quote._id} className={`p-4 ${isDarkMode ? 'hover:bg-gray-750' : 'hover:bg-gray-50'} transition-colors`}>
                                        <div className="flex justify-between items-start mb-3">
                                            <div>
                                                <div className={`text-sm font-semibold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                                                    {quote.id || quote._id.slice(-6)}
                                                </div>
                                                <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                                    {new Date(quote.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} • {new Date(quote.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                                                </div>
                                            </div>
                                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                                quote.status === 'PENDING'
                                                    ? 'bg-yellow-100 text-yellow-800' 
                                                    : quote.status === 'COMPLETED'
                                                    ? 'bg-green-100 text-green-800'
                                                    : quote.status === 'PARTIAL-COMPLETE'
                                                    ? 'bg-blue-100 text-blue-800'
                                                    : quote.status === 'REFFOLLOW'
                                                    ? 'bg-purple-100 text-purple-800'
                                                    : quote.status === 'CANCELLED'
                                                    ? 'bg-red-100 text-red-800'
                                                    : 'bg-gray-100 text-gray-800'
                                            }`}>
                                                {quote.status === 'PARTIAL-COMPLETE' ? 'Partial' : 
                                                 quote.status === 'REFFOLLOW' ? 'Ref Follow' :
                                                 quote.status ? quote.status.charAt(0) + quote.status.slice(1).toLowerCase() : 'Pending'}
                                            </span>
                                        </div>
                                        <div className="space-y-2 mb-3">
                                            <div className="flex justify-between text-sm">
                                                <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Recipient:</span>
                                                <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'} truncate ml-2`}>
                                                    {quote.recipient?.name || quote.recipient?.businessName || 'N/A'}
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Created By:</span>
                                                <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                                                    {quote.createdBy?.name || 'N/A'}
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Amount:</span>
                                                <span className={`font-semibold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                                    {formatCurrency(quote.totalAmount || quote.totalValue || 0)}
                                                </span>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleViewRecord(quote, 'tally')}
                                            className={`w-full px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                                isDarkMode 
                                                    ? 'bg-teal-600 hover:bg-teal-700 text-white' 
                                                    : 'bg-teal-500 hover:bg-teal-600 text-white'
                                            }`}
                                        >
                                            View Details
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Desktop Table View */}
                    <div className="hidden md:block overflow-x-auto -mx-4 sm:mx-0">
                        <div className="inline-block min-w-full align-middle">
                            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className={isDarkMode ? 'bg-gray-750' : 'bg-gray-50'}>
                                        <tr>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                                                Quote Number
                                            </th>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                                                Date
                                            </th>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                                                Recipient
                                            </th>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider hidden lg:table-cell`}>
                                                Created By
                                            </th>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                                                Total Amount
                                            </th>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                                                Status
                                            </th>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                            <tbody className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                                {tallyQuotations.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className={`px-3 sm:px-6 py-8 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                            No tally quotations found
                                        </td>
                                    </tr>
                                ) : (
                                    tallyQuotations.map((quote) => (
                                        <tr key={quote._id} className={`${isDarkMode ? 'hover:bg-gray-750' : 'hover:bg-gray-50'} transition-colors`}>
                                            <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                                                {quote.id || quote._id.slice(-6)}
                                            </td>
                                            <td className={`px-3 sm:px-6 py-4 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                                                <div>{new Date(quote.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                                                <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                                    {new Date(quote.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                                                </div>
                                            </td>
                                            <td className={`px-3 sm:px-6 py-4 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                                                <div className="max-w-xs truncate">{quote.recipient?.name || quote.recipient?.businessName || 'N/A'}</div>
                                            </td>
                                            <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} hidden lg:table-cell`}>
                                                {quote.createdBy?.name || 'N/A'}
                                            </td>
                                            <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-semibold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                                {formatCurrency(quote.totalAmount || quote.totalValue || 0)}
                                            </td>
                                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                    quote.status === 'PENDING'
                                                        ? 'bg-yellow-100 text-yellow-800' 
                                                        : quote.status === 'COMPLETED'
                                                        ? 'bg-green-100 text-green-800'
                                                        : quote.status === 'PARTIAL-COMPLETE'
                                                        ? 'bg-blue-100 text-blue-800'
                                                        : quote.status === 'REFFOLLOW'
                                                        ? 'bg-purple-100 text-purple-800'
                                                        : quote.status === 'CANCELLED'
                                                        ? 'bg-red-100 text-red-800'
                                                        : quote.status === 'REJECTED'
                                                        ? 'bg-gray-100 text-gray-800'
                                                        : 'bg-gray-100 text-gray-800'
                                                }`}>
                                                    {quote.status === 'PARTIAL-COMPLETE' ? 'Partial Complete' : 
                                                     quote.status === 'REFFOLLOW' ? 'Ref Follow' :
                                                     quote.status ? quote.status.charAt(0) + quote.status.slice(1).toLowerCase() : 'Pending'}
                                                </span>
                                            </td>
                                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm">
                                                <button
                                                    onClick={() => handleViewRecord(quote, 'tally')}
                                                    className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                                                        isDarkMode 
                                                            ? 'bg-teal-600 hover:bg-teal-700 text-white' 
                                                            : 'bg-teal-500 hover:bg-teal-600 text-white'
                                                    }`}
                                                >
                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                            </div>
                        </div>
                    </div>
                    <PaginationControls 
                        currentPage={tallyQuotationPage}
                        totalItems={tallyQuotationTotal}
                        onPageChange={setTallyQuotationPage}
                    />
                </div>
                )}
            </div>

            {/* View Details Modal - Tally Quotation (exact copy from TallyQuotation.js) */}
            {showModal && selectedRecord && selectedRecord.type === 'tally' && (
                <div className="fixed inset-0 z-50 bg-black bg-opacity-60 flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-xl w-full max-w-6xl flex flex-col h-[90vh] shadow-2xl animate-fadeIn">
                        {/* Header */}
                        <div className="bg-gradient-to-r from-indigo-600 to-blue-700 px-6 py-4 flex justify-between items-center flex-shrink-0 rounded-t-xl">
                            <div className="flex items-center space-x-4">
                                <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-white">Quotation {formatId(selectedRecord?.id)}</h2>
                                    <p className="text-xs text-blue-100 opacity-80">Created on {new Date(selectedRecord.createdAt).toLocaleDateString()}</p>
                                </div>
                            </div>
                            <button 
                                onClick={closeModal}
                                className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
                                aria-label="Close dialog"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Content - Scrollable */}
                        <div className="overflow-y-auto flex-grow">
                            {/* Status Bar */}
                            <div className="bg-gradient-to-r from-gray-50 to-blue-50 px-6 py-3 sticky top-0 z-10 shadow-sm border-b border-gray-200 flex items-center justify-between">
                                <div className="flex items-center space-x-4">
                                    <span className="bg-green-100 text-green-800 text-xs font-semibold px-2.5 py-0.5 rounded-full flex items-center">
                                        <span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                                        Active
                                    </span>
                                    <span className="text-gray-500 text-sm">
                                        Total Items: {selectedRecord.sections?.reduce((acc, section) => acc + section.items.length, 0) || 0}
                                    </span>
                                </div>
                                <div className="text-sm font-medium text-gray-900">
                                    Prepared by: {selectedRecord.createdBy?.name || 'Unknown'}
                                </div>
                            </div>

                            <div className="p-6 space-y-8">
                                {/* Overview Cards */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {/* Recipient Card */}
                                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                                        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                                            <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wide flex items-center">
                                                <svg className="w-4 h-4 mr-1 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                                                </svg>
                                                Recipient
                                            </h3>
                                        </div>
                                        <div className="p-4">
                                            <div className="mb-3 pb-3 border-b border-gray-100">
                                                <div className="text-xs text-gray-500 mb-1">Type</div>
                                                <div className="font-medium">{selectedRecord.recipientType}</div>
                                            </div>
                                            <div>
                                                <div className="text-xs text-gray-500 mb-1">Name</div>
                                                <div className="font-medium">
                                                    {selectedRecord.recipientType === 'Customer' 
                                                        ? selectedRecord.recipient?.name 
                                                        : selectedRecord.recipient?.businessName}
                                                </div>
                                                {selectedRecord.recipient?.phone && (
                                                    <div className="text-sm text-gray-500 mt-1">
                                                        {selectedRecord.recipient?.phone}
                                                    </div>
                                                )}
                                            </div>
                                            {selectedRecord.siteAddress && (
                                                <div className="mt-3 pt-3 border-t border-gray-100">
                                                    <div className="text-xs text-gray-500 mb-1">Site Address</div>
                                                    <div className="font-medium text-gray-800 whitespace-pre-line">
                                                        {selectedRecord.siteAddress}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    
                                    {/* Amount Card */}
                                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                                        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                                            <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wide flex items-center">
                                                <svg className="w-4 h-4 mr-1 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                                Amount Details
                                            </h3>
                                        </div>
                                        <div className="p-4 space-y-3">
                                            <div className="flex justify-between items-center">
                                                <div className="text-sm text-gray-500">Listed Price:</div>
                                                <div className="font-medium">₹{formatNumberWithCommas(selectedRecord.listedPriceGrandTotal || 0)}</div>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <div className="text-sm text-gray-500">Offer Price:</div>
                                                <div className="font-medium text-purple-700">₹{formatNumberWithCommas(selectedRecord.offerPriceGrandTotal || 0)}</div>
                                            </div>
                                            <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                                                <div className="text-sm font-medium">Total Amount:</div>
                                                <div className="text-lg font-bold text-green-700">₹{formatNumberWithCommas(selectedRecord.totalAmount || 0)}</div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Salesmen Card */}
                                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                                        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                                            <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wide flex items-center">
                                                <svg className="w-4 h-4 mr-1 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                                                </svg>
                                                Team
                                            </h3>
                                        </div>
                                        <div className="p-4">
                                            <div className="space-y-3">
                                                <div>
                                                    <div className="text-xs text-gray-500 mb-1">Salesmen</div>
                                                    {selectedRecord.salesman && selectedRecord.salesman.length > 0 ? (
                                                        <div className="flex flex-wrap gap-2">
                                                            {selectedRecord.salesman.map((salesman) => (
                                                                <div key={salesman._id} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                                    {salesman.name}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <div className="text-sm italic text-gray-500">No salesmen assigned</div>
                                                    )}
                                                </div>
                                                
                                                <div>
                                                    <div className="text-xs text-gray-500 mb-1">Reference</div>
                                                    <div className="font-medium">
                                                        {selectedRecord.reference?.name || 'Not specified'}
                                                    </div>
                                                </div>
                                                
                                                <div>
                                                    <div className="text-xs text-gray-500 mb-1">Transporter</div>
                                                    <div className="font-medium">
                                                        {selectedRecord.transporterName?.name || 'Not specified'}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Additional Charges */}
                                <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                                    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                                        <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wide flex items-center">
                                            <svg className="w-4 h-4 mr-1 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                            </svg>
                                            Additional Charges
                                        </h3>
                                    </div>
                                    
                                    <div className="p-4">
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                            <div className="bg-gray-50 rounded p-3">
                                                <div className="text-xs text-gray-500 mb-1">Cartage</div>
                                                <div className="font-medium">₹{formatNumberWithCommas(selectedRecord.cartage || 0)}</div>
                                                <div className="text-xs text-gray-500 mt-1">+GST: ₹{formatNumberWithCommas((selectedRecord.cartage || 0) * 0.18)}</div>
                                            </div>
                                            
                                            <div className="bg-gray-50 rounded p-3">
                                                <div className="text-xs text-gray-500 mb-1">Packing & Forwarding</div>
                                                <div className="font-medium">₹{formatNumberWithCommas(selectedRecord.packingAndForwarding || 0)}</div>
                                                <div className="text-xs text-gray-500 mt-1">+GST: ₹{formatNumberWithCommas((selectedRecord.packingAndForwarding || 0) * 0.18)}</div>
                                            </div>
                                            
                                            <div className="bg-gray-50 rounded p-3">
                                                <div className="text-xs text-gray-500 mb-1">Labour</div>
                                                <div className="font-medium">₹{formatNumberWithCommas(selectedRecord.labour || 0)}</div>
                                                <div className="text-xs text-gray-500 mt-1">+GST: ₹{formatNumberWithCommas((selectedRecord.labour || 0) * 0.18)}</div>
                                            </div>
                                            
                                            <div className="bg-gray-50 rounded p-3">
                                                <div className="text-xs text-gray-500 mb-1">Round Off</div>
                                                <div className="font-medium">₹{formatNumberWithCommas(selectedRecord.roundOff || 0)}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Remarks */}
                                {selectedRecord.remarks && (
                                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                        <h3 className="text-sm font-semibold text-yellow-800 flex items-center mb-2">
                                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                                            </svg>
                                            Remarks
                                        </h3>
                                        <p className="text-sm text-yellow-800">{selectedRecord.remarks}</p>
                                    </div>
                                )}

                                {/* Sections */}
                                <div className="space-y-6">
                                    <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Quotation Items</h3>
                                    
                                    <div className="space-y-6">
                                        {selectedRecord.sections && selectedRecord.sections.map((section, sectionIndex) => (
                                            <div key={sectionIndex} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                                                <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                                                    <h4 className="font-medium text-gray-800 text-sm md:text-base">
                                                        Section {sectionIndex + 1}: {section.name || 'Untitled Section'}
                                                    </h4>
                                                    <span className="text-sm font-medium text-blue-700">
                                                        ₹{formatNumberWithCommas(section.offerPriceTotal || 0)}
                                                    </span>
                                                </div>
                                                
                                                <div className="overflow-x-auto -mx-4 sm:mx-0">
                                                    <div className="inline-block min-w-full align-middle">
                                                    <table className="min-w-full divide-y divide-gray-200">
                                                        <thead className="bg-gray-50">
                                                            <tr>
                                                                <th scope="col" className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                                                                <th scope="col" className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Application Area</th>
                                                                <th scope="col" className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                                                                <th scope="col" className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Price</th>
                                                                <th scope="col" className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Discount</th>
                                                                <th scope="col" className="px-2 sm:px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="bg-white divide-y divide-gray-100">
                                                            {section.items && section.items.map((item, itemIndex) => (
                                                                <tr key={itemIndex} className="hover:bg-gray-50">
                                                                    <td className="px-2 sm:px-4 py-3">
                                                                        <div className="font-medium text-gray-900 text-sm max-w-xs truncate">{item.customName || item.itemName}</div>
                                                                        {item.item?.Name && item.customName && item.customName !== item.item.Name && (
                                                                            <div className="text-xs text-gray-500 mt-1">{item.item.Name}</div>
                                                                        )}
                                                                    </td>
                                                                    <td className="px-2 sm:px-4 py-3 text-sm text-gray-500 hidden md:table-cell">
                                                                        {item.applicationArea || '-'}
                                                                    </td>
                                                                    <td className="px-2 sm:px-4 py-3">
                                                                        {item.parameter?.calculationType === 'pcsPerBox' ? (
                                                                            <div className="text-sm text-gray-900">
                                                                                <span className="font-medium">{item.boxes}</span> Box, <span className="font-medium">{item.pieces}</span> Pcs
                                                                                {item.coverageArea > 0 && (
                                                                                    <div className="text-xs text-gray-500 mt-1">
                                                                                        Coverage: {item.coverageArea} {item.coverageAreaUnit || 'sqft'}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        ) : (
                                                                            <div className="text-sm text-gray-900">
                                                                                {item.quantity} {item.unit || 'pcs'}
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                    <td className="px-2 sm:px-4 py-3 text-sm text-gray-900 hidden sm:table-cell">
                                                                        ₹{formatNumberWithCommas(item.listedPrice)}
                                                                    </td>
                                                                    <td className="px-2 sm:px-4 py-3 hidden md:table-cell">
                                                                        {item.discount > 0 ? (
                                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                                {item.discount}%
                                                                            </span>
                                                                        ) : (
                                                                            <span className="text-sm text-gray-400">-</span>
                                                                        )}
                                                                    </td>
                                                                    <td className="px-2 sm:px-4 py-3 text-right">
                                                                        <div className="text-sm font-medium text-gray-900">
                                                                            ₹{formatNumberWithCommas(item.offerPriceTotal)}
                                                                        </div>
                                                                        {item.discount > 0 && (
                                                                            <div className="text-xs text-gray-500 line-through">
                                                                                ₹{formatNumberWithCommas(item.listedPriceTotal)}
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                        <tfoot className="bg-gray-50">
                                                            <tr>
                                                                <td colSpan="5" className="px-2 sm:px-4 py-3 text-sm font-medium text-gray-700 text-right">
                                                                    Section Total:
                                                                </td>
                                                                <td className="px-2 sm:px-4 py-3 text-right">
                                                                    <div className="font-bold text-blue-700">
                                                                        ₹{formatNumberWithCommas(section.offerPriceTotal)}
                                                                    </div>
                                                                    {section.offerPriceTotal !== section.listedPriceTotal && (
                                                                        <div className="text-xs text-gray-500 line-through">
                                                                            ₹{formatNumberWithCommas(section.listedPriceTotal)}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        </tfoot>
                                                    </table>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    
                                    {/* Summary */}
                                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div className="space-y-1">
                                                <div className="text-xs text-gray-500">Items</div>
                                                <div className="text-xl font-semibold text-gray-800">
                                                    {selectedRecord.sections?.reduce((total, section) => total + section.items.length, 0) || 0}
                                                </div>
                                            </div>
                                            <div className="space-y-1">
                                                <div className="text-xs text-gray-500">Sections</div>
                                                <div className="text-xl font-semibold text-gray-800">
                                                    {selectedRecord.sections?.length || 0}
                                                </div>
                                            </div>
                                            <div className="space-y-1">
                                                <div className="text-xs text-gray-500">Grand Total</div>
                                                <div className="text-xl font-bold text-green-700">
                                                    ₹{formatNumberWithCommas(selectedRecord.totalAmount || 0)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="border-t border-gray-200 p-4 flex justify-end gap-3 bg-gray-50 flex-shrink-0 rounded-b-xl">
                            <button
                                onClick={closeModal}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 flex items-center transition-colors shadow-sm"
                            >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* View Details Modal - Delivery Order & Quotation New */}
            {showModal && selectedRecord && (selectedRecord.type === 'delivery' || selectedRecord.type === 'quotation') && (
                <div className="fixed inset-0 z-50 bg-black bg-opacity-60 flex items-center justify-center p-2 sm:p-4 backdrop-blur-sm">
                    <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl w-full max-w-7xl flex flex-col max-h-[95vh] sm:h-[90vh] shadow-2xl animate-fadeIn`}>
                        {/* Responsive Header */}
                        <div className={`${isDarkMode ? 'bg-gradient-to-r from-gray-700 to-gray-600' : 'bg-gradient-to-r from-indigo-600 to-blue-700'} px-3 sm:px-6 py-3 sm:py-4 flex justify-between items-start sm:items-center flex-shrink-0 rounded-t-xl`}>
                            <div className="flex items-start sm:items-center space-x-2 sm:space-x-4 flex-1 min-w-0">
                                <div className="bg-white bg-opacity-20 p-1.5 sm:p-2 rounded-lg flex-shrink-0">
                                    <svg className="w-4 h-4 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                </div>
                                <div className="min-w-0 flex-1">
                                    <h2 className="text-sm sm:text-xl font-bold text-white truncate">
                                        {selectedRecord.type === 'delivery' ? `Order Details: ${selectedRecord.doNumber}` :
                                         `Quotation #${selectedRecord.id || selectedRecord._id}`}
                                    </h2>
                                    <p className="text-xs text-blue-100 opacity-80 truncate">
                                        Created on {new Date(selectedRecord.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} by {selectedRecord.createdBy?.name || 'Unknown'}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={closeModal}
                                className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1.5 sm:p-2 transition-colors flex-shrink-0 ml-2"
                                aria-label="Close dialog"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Content - Scrollable with responsive padding */}
                        <div className="overflow-y-auto flex-grow">
                            <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
                                {/* Order Information & Recipient Information Grid (for Delivery Orders) */}
                                {selectedRecord.type === 'delivery' && (
                                    <>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                                            {/* Order Information */}
                                            <div className={`${isDarkMode ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'} rounded-lg border p-3 sm:p-4`}>
                                                <div className="flex items-center mb-2 sm:mb-3">
                                                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                    </svg>
                                                    <h3 className={`text-xs sm:text-sm font-semibold ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>Order Information</h3>
                                                </div>
                                                <div className="space-y-1.5 sm:space-y-2">
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>DO Number:</div>
                                                        <div className={`text-xs sm:text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'} truncate`}>{selectedRecord.doNumber}</div>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Status:</div>
                                                        <div>
                                                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                                                                selectedRecord.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                                                                selectedRecord.status === 'Completed' ? 'bg-green-100 text-green-800' :
                                                                'bg-gray-100 text-gray-800'
                                                            }`}>
                                                                {selectedRecord.status}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Created On:</div>
                                                        <div className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                                            {new Date(selectedRecord.createdAt).toLocaleString('en-IN', { 
                                                                day: '2-digit', 
                                                                month: '2-digit', 
                                                                year: 'numeric',
                                                                hour: '2-digit',
                                                                minute: '2-digit',
                                                                hour12: true
                                                            })}
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Created By:</div>
                                                        <div className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                                            {selectedRecord.createdBy?.name || 'Unknown'}
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Mode of Sales:</div>
                                                        <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                            {selectedRecord.paymentMode || selectedRecord.modeOfSales || '-'}
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Manual DO:</div>
                                                        <div className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                                            {selectedRecord.manualDoNumber || '-'}
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>PO Number:</div>
                                                        <div className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                                            {selectedRecord.poNumber || '-'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Recipient Information */}
                                            <div className={`${isDarkMode ? 'bg-purple-900/20 border-purple-700' : 'bg-purple-50 border-purple-200'} rounded-lg border p-4`}>
                                                <div className="flex items-center mb-3">
                                                    <svg className="w-5 h-5 text-purple-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                    </svg>
                                                    <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-purple-300' : 'text-purple-800'}`}>Recipient Information</h3>
                                                </div>
                                                <div className="space-y-2">
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Type:</div>
                                                        <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                            {selectedRecord.recipientType || 'Customer'}
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Name:</div>
                                                        <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                            {selectedRecord.recipient?.name || selectedRecord.recipient?.businessName || 'N/A'}
                                                        </div>
                                                    </div>
                                                    {selectedRecord.recipient?.mobileNumber && (
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Phone:</div>
                                                            <div className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                                                {selectedRecord.recipient.mobileNumber}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {selectedRecord.deliveryAddress && (
                                                        <div className="col-span-2">
                                                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Delivery Address:</div>
                                                            <div className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} whitespace-pre-line`}>
                                                                {selectedRecord.deliveryAddress}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Team Information */}
                                            <div className={`${isDarkMode ? 'bg-gray-750 border-gray-600' : 'bg-white border-gray-200'} rounded-lg border p-4`}>
                                                <div className="flex items-center mb-3">
                                                    <svg className="w-5 h-5 text-indigo-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                                    </svg>
                                                    <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-800'} uppercase tracking-wide`}>TEAM</h3>
                                                </div>
                                                <div className="space-y-2">
                                                    <div>
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Salesmen</div>
                                                        <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                            {selectedRecord.salesman?.map(s => s.name).join(', ') || 
                                                             selectedRecord.salesman?.[0]?.name || 
                                                             selectedRecord.salesmanName ||
                                                             'Not specified'}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Reference</div>
                                                        <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                            {selectedRecord.reference?.name || 
                                                             selectedRecord.referenceName ||
                                                             'Not specified'}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Transporter</div>
                                                        <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                            {selectedRecord.transporterName?.name || 
                                                             selectedRecord.transporter?.name ||
                                                             selectedRecord.transporter ||
                                                             'Not specified'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* Team Information (for Delivery Orders) - OLD VERSION TO REMOVE */}
                                {selectedRecord.type === 'delivery' && false && (
                                    <div className={`${isDarkMode ? 'bg-gray-750 border-gray-600' : 'bg-white border-gray-200'} rounded-lg border p-4`}>
                                        <div className="flex items-center mb-3">
                                            <svg className="w-5 h-5 text-indigo-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                            </svg>
                                            <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-800'} uppercase tracking-wide`}>TEAM</h3>
                                        </div>
                                        <div className="space-y-3">
                                            <div>
                                                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Salesmen</div>
                                                <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                    {selectedRecord.salesmen?.map(s => s.name).join(', ') || 
                                                     selectedRecord.salesmen?.[0]?.name || 
                                                     selectedRecord.salesmanName ||
                                                     'Not specified'}
                                                </div>
                                            </div>
                                            <div>
                                                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Reference</div>
                                                <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                    {selectedRecord.reference?.name || 
                                                     selectedRecord.referenceName ||
                                                     'Not specified'}
                                                </div>
                                            </div>
                                            <div>
                                                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Transporter</div>
                                                <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                    {selectedRecord.transporterName?.name || 
                                                     selectedRecord.transporter?.name ||
                                                     selectedRecord.transporter ||
                                                     'Not specified'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* For Quotation New - Enhanced with more details */}
                                {selectedRecord.type === 'quotation' && (
                                    <>
                                        {/* Quotation Information, Recipient Information & Team Grid */}
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            {/* Quotation Information */}
                                            <div className={`${isDarkMode ? 'bg-indigo-900/20 border-indigo-700' : 'bg-indigo-50 border-indigo-200'} rounded-lg border p-4`}>
                                                <div className="flex items-center mb-3">
                                                    <svg className="w-5 h-5 text-indigo-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                    </svg>
                                                    <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-indigo-300' : 'text-indigo-800'}`}>Quotation Information</h3>
                                                </div>
                                                <div className="space-y-2">
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Quotation ID:</div>
                                                        <div className={`text-sm font-bold ${isDarkMode ? 'text-indigo-400' : 'text-indigo-700'}`}>
                                                            {formatId(selectedRecord.id) || selectedRecord._id?.slice(-6)}
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Status:</div>
                                                        <div>
                                                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                                                                selectedRecord.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                                                                selectedRecord.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                                                                selectedRecord.status === 'CANCELLED' ? 'bg-red-100 text-red-800' :
                                                                selectedRecord.status === 'PARTIAL-COMPLETE' ? 'bg-blue-100 text-blue-800' :
                                                                selectedRecord.status === 'REFFOLLOW' ? 'bg-purple-100 text-purple-800' :
                                                                'bg-gray-100 text-gray-800'
                                                            }`}>
                                                                {selectedRecord.status || 'PENDING'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Created On:</div>
                                                        <div className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                                            {new Date(selectedRecord.createdAt).toLocaleString('en-IN', { 
                                                                day: '2-digit', 
                                                                month: '2-digit', 
                                                                year: 'numeric',
                                                                hour: '2-digit',
                                                                minute: '2-digit',
                                                                hour12: true
                                                            })}
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Created By:</div>
                                                        <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                            {selectedRecord.createdBy?.name || 'Unknown'}
                                                        </div>
                                                    </div>
                                                    {selectedRecord.updatedAt && selectedRecord.updatedAt !== selectedRecord.createdAt && (
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Last Updated:</div>
                                                            <div className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                                                {new Date(selectedRecord.updatedAt).toLocaleString('en-IN', { 
                                                                    day: '2-digit', 
                                                                    month: 'short',
                                                                    hour: '2-digit',
                                                                    minute: '2-digit'
                                                                })}
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Items:</div>
                                                        <div className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                            {selectedRecord.products?.length || 0}
                                                        </div>
                                                    </div>
                                                    {selectedRecord.sections && selectedRecord.sections.length > 0 && (
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Sections:</div>
                                                            <div className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                                                {selectedRecord.sections.length}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Recipient Information */}
                                            <div className={`${isDarkMode ? 'bg-purple-900/20 border-purple-700' : 'bg-purple-50 border-purple-200'} rounded-lg border p-4`}>
                                                <div className="flex items-center mb-3">
                                                    <svg className="w-5 h-5 text-purple-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                    </svg>
                                                    <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-purple-300' : 'text-purple-800'}`}>Recipient Details</h3>
                                                </div>
                                                <div className="space-y-2">
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Type:</div>
                                                        <div>
                                                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                                                                selectedRecord.recipientType === 'Customer' ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'
                                                            }`}>
                                                                {selectedRecord.recipientType || 'Customer'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Name:</div>
                                                        <div className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                            {selectedRecord.recipient?.name || selectedRecord.recipient?.businessName || 'N/A'}
                                                        </div>
                                                    </div>
                                                    {selectedRecord.recipient?.mobileNumber && (
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Phone:</div>
                                                            <div className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                                                {selectedRecord.recipient.mobileNumber}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {selectedRecord.recipient?.email && (
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Email:</div>
                                                            <div className={`text-xs ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} truncate`}>
                                                                {selectedRecord.recipient.email}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {selectedRecord.recipient?.station?.name && (
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Station:</div>
                                                            <div className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                                                {selectedRecord.recipient.station.name}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {selectedRecord.siteAddress && (
                                                        <div className="col-span-2 pt-2 border-t border-purple-300">
                                                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Site Address:</div>
                                                            <div className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} whitespace-pre-line`} 
                                                                 style={{ maxHeight: '4.5rem', overflow: 'auto' }}>
                                                                {selectedRecord.siteAddress}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Team & Financial Summary */}
                                            <div className={`${isDarkMode ? 'bg-green-900/20 border-green-700' : 'bg-green-50 border-green-200'} rounded-lg border p-4`}>
                                                <div className="flex items-center mb-3">
                                                    <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                                    </svg>
                                                    <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-green-300' : 'text-green-800'}`}>Team & Amount</h3>
                                                </div>
                                                <div className="space-y-2">
                                                    <div>
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Salesmen</div>
                                                        <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                            {selectedRecord.salesmen?.length > 0 
                                                                ? selectedRecord.salesmen.map(s => s.name).join(', ')
                                                                : 'Not assigned'}
                                                        </div>
                                                    </div>
                                                    {selectedRecord.reference?.name && (
                                                        <div>
                                                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Reference</div>
                                                            <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                                {selectedRecord.reference.name}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {selectedRecord.interior?.name && (
                                                        <div>
                                                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Interior Designer</div>
                                                            <div className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                                                {selectedRecord.interior.name}
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div className="pt-2 border-t border-green-300 space-y-1">
                                                        {selectedRecord.subTotal && (
                                                            <div className="flex justify-between items-center">
                                                                <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Subtotal:</span>
                                                                <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                                                    ₹{formatNumberWithCommas(selectedRecord.subTotal)}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {selectedRecord.gstAmount && selectedRecord.gstAmount > 0 && (
                                                            <div className="flex justify-between items-center">
                                                                <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>GST:</span>
                                                                <span className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                                                    ₹{formatNumberWithCommas(selectedRecord.gstAmount)}
                                                                </span>
                                                            </div>
                                                        )}
                                                        <div className="flex justify-between items-center pt-1 border-t border-green-400">
                                                            <span className={`text-xs font-bold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Grand Total:</span>
                                                            <span className={`text-lg font-bold ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
                                                                ₹{formatNumberWithCommas(selectedRecord.totalAmount || selectedRecord.totalValue || selectedRecord.grandTotal || 0)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* Items Table - Comprehensive (matching screenshot) */}
                                {((selectedRecord.type === 'delivery' && selectedRecord.items && selectedRecord.items.length > 0) ||
                                  (selectedRecord.type === 'quotation' && selectedRecord.products && selectedRecord.products.length > 0)) && (
                                    <div className="rounded-lg shadow-sm overflow-hidden border border-gray-200">
                                        <div className="px-3 sm:px-4 py-2 sm:py-3 bg-gradient-to-r from-gray-700 to-gray-800 flex items-center border-b border-gray-600">
                                            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                            </svg>
                                            <h3 className="text-xs sm:text-sm font-semibold text-white uppercase tracking-wide">Items ({selectedRecord.type === 'delivery' ? selectedRecord.items?.length : selectedRecord.products?.length})</h3>
                                        </div>
                                        
                                        {/* Desktop Table View */}
                                        <div className="hidden md:block overflow-x-auto bg-white">
                                            <table className="min-w-full divide-y divide-gray-200">
                                                {/* Different headers for delivery vs quotation */}
                                                {selectedRecord.type === 'delivery' ? (
                                                    <thead className="bg-gray-50 border-b-2 border-gray-200">
                                                        <tr>
                                                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">ITEM</th>
                                                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider hidden xl:table-cell">PRINT TYPE</th>
                                                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider hidden 2xl:table-cell">DESCRIPTION</th>
                                                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">HSN</th>
                                                            <th className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">QTY</th>
                                                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">UNIT</th>
                                                            <th className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">RATE</th>
                                                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">DISCOUNT</th>
                                                            {user?.user?.isTT && (
                                                                <th className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider hidden xl:table-cell">TT</th>
                                                            )}
                                                            <th className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">AMOUNT</th>
                                                        </tr>
                                                    </thead>
                                                ) : (
                                                    <thead className="bg-gray-50 border-b-2 border-gray-200">
                                                        <tr>
                                                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">ITEM NAME</th>
                                                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">HSN</th>
                                                            <th className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">QTY</th>
                                                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">UNIT</th>
                                                            <th className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider hidden lg:table-cell">LISTED PRICE</th>
                                                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">DISCOUNT</th>
                                                            <th className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">OFFER PRICE</th>
                                                            <th className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">AMOUNT</th>
                                                        </tr>
                                                    </thead>
                                                )}
                                                <tbody className="bg-white divide-y divide-gray-100">
                                                    {selectedRecord.products?.map((product, index) => {
                                                        // Skip if product is undefined or null
                                                        if (!product) return null;
                                                        
                                                        // For delivery orders, calculate TT amount from discount
                                                        const itemQty = product.quantity || 0;
                                                        const itemRate = product.rate || product.unitPrice || product.price || 0;
                                                        const itemDiscount = product.discount || 0;
                                                        const ttAmount = selectedRecord.type === 'delivery' 
                                                            ? (itemQty * itemRate * itemDiscount / 100) 
                                                            : (product.ttAmount || 0);
                                                        
                                                        // Render different columns based on type
                                                        if (selectedRecord.type === 'delivery') {
                                                            return (
                                                            <tr key={index} className="hover:bg-gray-50 transition-colors">
                                                                <td className="px-3 py-3 text-sm font-medium text-gray-900">
                                                                    <div className="max-w-[200px] truncate">{product.item?.Name || product.item?.name || product.item?.productName || product.item?.itemName || product.Name || product.name || product.tileId?.name || 'N/A'}</div>
                                                                </td>
                                                                <td className="px-3 py-3 hidden xl:table-cell">
                                                                    {product.printType && (
                                                                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                                                                            product.printType === 'item' ? 'bg-blue-100 text-blue-800' :
                                                                            product.printType === 'both' ? 'bg-green-100 text-green-800' :
                                                                            'bg-gray-100 text-gray-800'
                                                                        }`}>
                                                                            {product.printType}
                                                                        </span>
                                                                    )}
                                                                </td>
                                                                <td className="px-3 py-3 text-sm text-gray-600 hidden 2xl:table-cell">
                                                                    <div className="max-w-[150px] truncate">{product.description || product.itemDescription || '-'}</div>
                                                                </td>
                                                                <td className="px-3 py-3 text-sm text-gray-700">
                                                                    {product.hsn || product.hsnCode || '-'}
                                                                </td>
                                                                <td className="px-3 py-3 text-right text-sm font-medium text-gray-900">
                                                                    {formatNumberWithCommas(product.quantity || 0)}
                                                                </td>
                                                                <td className="px-3 py-3 text-sm text-gray-600">
                                                                    {product.units || product.unit || 'box'}
                                                                </td>
                                                                <td className="px-3 py-3 text-right text-sm text-gray-900">
                                                                    ₹{formatNumberWithCommas(product.rate || product.unitPrice || product.price || 0)}
                                                                </td>
                                                                <td className="px-3 py-3">
                                                                    {product.discount && parseFloat(product.discount) > 0 ? (
                                                                        <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                                            {product.discount}%
                                                                        </span>
                                                                    ) : (
                                                                        <span className="text-sm text-gray-400">-</span>
                                                                    )}
                                                                </td>
                                                                {user?.user?.isTT && (
                                                                    <td className="px-3 py-3 text-right text-sm text-gray-700 hidden xl:table-cell">
                                                                        {ttAmount && parseFloat(ttAmount) > 0 ? 
                                                                            `₹${formatNumberWithCommas(ttAmount)}` : 
                                                                            '-'
                                                                        }
                                                                    </td>
                                                                )}
                                                                <td className="px-3 py-3 text-right text-sm font-bold text-gray-900">
                                                                    ₹{formatNumberWithCommas(product.amountAfterDiscount || product.totalPrice || product.amount || (product.quantity * (product.rate || product.unitPrice || product.price || 0)) || 0)}
                                                                </td>
                                                            </tr>
                                                            );
                                                        } else {
                                                            // Quotation row with descriptive pricing
                                                            const listedPrice = product.listedPrice || product.unitPrice || product.price || 0;
                                                            const offerPrice = product.rate || product.unitPrice || product.price || 0;
                                                            
                                                            return (
                                                            <tr key={index} className="hover:bg-gray-50 transition-colors">
                                                                <td className="px-3 py-3 text-sm font-medium text-gray-900">
                                                                    <div className="max-w-[200px] truncate">{product.skuCode || product.item?.name || product.item?.productName || product.item?.itemName || product.name || 'N/A'}</div>
                                                                    {product.skuDescription && (
                                                                        <div className="text-xs mt-1 text-gray-600 max-w-[200px] truncate">
                                                                            {product.item.skuDescription}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td className="px-3 py-3 text-sm text-gray-700">
                                                                    {product.hsn || product.hsnCode || '-'}
                                                                </td>
                                                                <td className="px-3 py-3 text-right text-sm font-medium text-gray-900">
                                                                    {formatNumberWithCommas(product.quantity || 0)}
                                                                </td>
                                                                <td className="px-3 py-3 text-sm text-gray-600">
                                                                    {product.units || product.unit || 'pcs'}
                                                                </td>
                                                                <td className="px-3 py-3 text-right text-sm text-gray-500 line-through hidden lg:table-cell">
                                                                    {listedPrice > 0 ? `₹${formatNumberWithCommas(listedPrice)}` : '-'}
                                                                </td>
                                                                <td className="px-3 py-3">
                                                                    {product.discount && parseFloat(product.discount) > 0 ? (
                                                                        <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                                            {product.discount}%
                                                                        </span>
                                                                    ) : (
                                                                        <span className="text-sm text-gray-400">-</span>
                                                                    )}
                                                                </td>
                                                                <td className="px-3 py-3 text-right text-sm font-bold text-blue-700">
                                                                    ₹{formatNumberWithCommas(offerPrice)}
                                                                </td>
                                                                <td className="px-3 py-3 text-right text-sm font-bold text-gray-900">
                                                                    ₹{formatNumberWithCommas(product.totalPrice || product.amount || (product.quantity * offerPrice) || 0)}
                                                                </td>
                                                            </tr>
                                                            );
                                                        }
                                                    })}
                                                </tbody>
                                                <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                                                    <tr>
                                                        {/* Adjust colspan based on delivery vs quotation and TT visibility */}
                                                        <td colSpan={
                                                            selectedRecord.type === 'delivery' 
                                                                ? (user?.user?.isTT ? "9" : "8")
                                                                : "7"
                                                        } className="px-3 py-3 text-right text-sm font-bold text-gray-700">
                                                            Total:
                                                        </td>
                                                        <td className="px-3 py-3 text-right text-base font-bold text-blue-700">
                                                            ₹{formatNumberWithCommas(
                                                                (selectedRecord.type === 'delivery' ? selectedRecord.items : selectedRecord.products)?.reduce((sum, p) => 
                                                                    sum + (p.amountAfterDiscount || p.totalPrice || p.amount || (p.quantity * (p.rate || p.unitPrice || p.price || 0)) || 0), 0
                                                                ) || 0
                                                            )}
                                                        </td>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                        
                                        {/* Mobile/Tablet Card View */}
                                        <div className="md:hidden divide-y divide-gray-200 bg-white">
                                            {selectedRecord.products?.map((product, index) => {
                                                if (!product) return null;
                                                
                                                const itemQty = product.quantity || 0;
                                                const itemRate = product.rate || product.unitPrice || product.price || 0;
                                                const itemDiscount = product.discount || 0;
                                                const ttAmount = selectedRecord.type === 'delivery' 
                                                    ? (itemQty * itemRate * itemDiscount / 100) 
                                                    : (product.ttAmount || 0);
                                                
                                                return (
                                                    <div key={index} className="p-3 hover:bg-gray-50">
                                                        {/* Item Name */}
                                                        <div className="font-medium text-sm mb-2 text-gray-900">
                                                            {product.item?.Name || product.item?.name || product.skuCode || product.name || 'N/A'}
                                                        </div>
                                                        
                                                        {/* Item Details Grid */}
                                                        <div className="grid grid-cols-2 gap-2 text-xs">
                                                            {selectedRecord.type === 'delivery' ? (
                                                                <>
                                                                    {product.printType && (
                                                                        <div>
                                                                            <span className="text-gray-600">Print Type:</span>
                                                                            <span className={`ml-1 inline-block px-2 py-0.5 rounded font-medium ${
                                                                                product.printType === 'item' ? 'bg-blue-100 text-blue-800' :
                                                                                product.printType === 'both' ? 'bg-green-100 text-green-800' :
                                                                                'bg-gray-100 text-gray-800'
                                                                            }`}>
                                                                                {product.printType}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                    <div>
                                                                        <span className="text-gray-600">HSN:</span>
                                                                        <span className="ml-1 text-gray-900">
                                                                            {product.hsn || product.hsnCode || '-'}
                                                                        </span>
                                                                    </div>
                                                                    <div>
                                                                        <span className="text-gray-600">Qty:</span>
                                                                        <span className="ml-1 font-medium text-gray-900">
                                                                            {formatNumberWithCommas(product.quantity || 0)} {product.units || product.unit || 'box'}
                                                                        </span>
                                                                    </div>
                                                                    <div>
                                                                        <span className="text-gray-600">Rate:</span>
                                                                        <span className="ml-1 text-gray-900">
                                                                            ₹{formatNumberWithCommas(product.rate || product.unitPrice || product.price || 0)}
                                                                        </span>
                                                                    </div>
                                                                    {product.discount && parseFloat(product.discount) > 0 && (
                                                                        <div>
                                                                            <span className="text-gray-600">Discount:</span>
                                                                            <span className="ml-1 inline-block px-2 py-0.5 rounded font-medium bg-green-100 text-green-800">
                                                                                {product.discount}%
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                    {user?.user?.isTT && ttAmount && parseFloat(ttAmount) > 0 && (
                                                                        <div>
                                                                            <span className="text-gray-600">TT:</span>
                                                                            <span className="ml-1 text-gray-700">
                                                                                ₹{formatNumberWithCommas(ttAmount)}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <div>
                                                                        <span className="text-gray-600">HSN:</span>
                                                                        <span className="ml-1 text-gray-900">
                                                                            {product.hsn || product.hsnCode || '-'}
                                                                        </span>
                                                                    </div>
                                                                    <div>
                                                                        <span className="text-gray-600">Qty:</span>
                                                                        <span className="ml-1 font-medium text-gray-900">
                                                                            {formatNumberWithCommas(product.quantity || 0)} {product.units || product.unit || 'pcs'}
                                                                        </span>
                                                                    </div>
                                                                    <div>
                                                                        <span className="text-gray-600">Listed Price:</span>
                                                                        <span className="ml-1 text-gray-600 line-through">
                                                                            ₹{formatNumberWithCommas(product.listedPrice || product.unitPrice || product.price || 0)}
                                                                        </span>
                                                                    </div>
                                                                    <div>
                                                                        <span className="text-gray-600">Offer Price:</span>
                                                                        <span className="ml-1 font-bold text-blue-700">
                                                                            ₹{formatNumberWithCommas(product.rate || product.unitPrice || product.price || 0)}
                                                                        </span>
                                                                    </div>
                                                                    {product.discount && parseFloat(product.discount) > 0 && (
                                                                        <div>
                                                                            <span className="text-gray-600">Discount:</span>
                                                                            <span className="ml-1 inline-block px-2 py-0.5 rounded font-medium bg-green-100 text-green-800">
                                                                                {product.discount}%
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                        
                                                        {/* Description (if available) */}
                                                        {product.description && selectedRecord.type === 'delivery' && (
                                                            <div className="mt-2 text-xs text-gray-600">
                                                                {product.description}
                                                            </div>
                                                        )}
                                                        
                                                        {/* Amount */}
                                                        <div className="mt-2 pt-2 border-t border-gray-200 flex justify-between items-center">
                                                            <span className="text-xs font-medium text-gray-600">Amount:</span>
                                                            <span className="text-sm font-bold text-gray-900">
                                                                ₹{formatNumberWithCommas(product.amountAfterDiscount || product.totalPrice || product.amount || (product.quantity * (product.rate || product.unitPrice || product.price || 0)) || 0)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            
                                            {/* Mobile Total */}
                                            <div className="p-3 bg-gray-100 border-t-2 border-gray-300">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-sm font-bold text-gray-700">Total:</span>
                                                    <span className="text-base font-bold text-blue-700">
                                                        ₹{formatNumberWithCommas(
                                                            (selectedRecord.type === 'delivery' ? selectedRecord.items : selectedRecord.products)?.reduce((sum, p) => 
                                                                sum + (p.amountAfterDiscount || p.totalPrice || p.amount || (p.quantity * (p.rate || p.unitPrice || p.price || 0)) || 0), 0
                                                            ) || 0
                                                        )}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Additional Charges, Other Details & Amounts Grid */}
                                {selectedRecord.type === 'delivery' && (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        {/* Additional Charges */}
                                        <div className={`${isDarkMode ? 'bg-green-900/20 border-green-700' : 'bg-green-50 border-green-200'} rounded-lg border p-4`}>
                                            <div className="flex items-center mb-3">
                                                <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-green-300' : 'text-green-800'}`}>Additional Charges</h3>
                                            </div>
                                            <div className="space-y-2">
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>P&F Charges:</div>
                                                    <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                        ₹{formatNumberWithCommas(selectedRecord.packingAndForwarding || selectedRecord.pnfCharges || 0)}
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Labour:</div>
                                                    <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                        ₹{formatNumberWithCommas(selectedRecord.labour || 0)}
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Cartage:</div>
                                                    <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                        ₹{formatNumberWithCommas(selectedRecord.cartage || 0)}
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Round Off:</div>
                                                    <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                        ₹{formatNumberWithCommas(selectedRecord.roundOff || 0)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Other Details */}
                                        <div className={`${isDarkMode ? 'bg-yellow-900/20 border-yellow-700' : 'bg-yellow-50 border-yellow-200'} rounded-lg border p-4`}>
                                            <div className="flex items-center mb-3">
                                                <svg className="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>Other Details</h3>
                                            </div>
                                            <div className="space-y-2">
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Vehicle Number:</div>
                                                    <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                        {selectedRecord.vehicleNumber || selectedRecord.vehicle?.vehicleNumber || '-'}
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Transporter:</div>
                                                    <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                        {selectedRecord.transporterName?.name || selectedRecord.transporter || '-'}
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Godown:</div>
                                                    <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                        {selectedRecord.godown?.name || selectedRecord.godownName || '-'}
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Reference:</div>
                                                    <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                        {selectedRecord.reference?.name || '-'}
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Payment Mode:</div>
                                                    <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                        {selectedRecord.paymentMode || selectedRecord.modeOfSales || '-'}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Amounts */}
                                        <div className={`${isDarkMode ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'} rounded-lg border p-4`}>
                                            <div className="flex items-center mb-3">
                                                <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                                </svg>
                                                <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>Amounts</h3>
                                            </div>
                                            <div className="space-y-2">
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Amount without GST:</div>
                                                    <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                        ₹{formatNumberWithCommas(selectedRecord.totalAmountWithoutGST || selectedRecord.amountWithoutGST || 0)}
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>GST Amount:</div>
                                                    <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                        ₹{formatNumberWithCommas(
                                                            selectedRecord.gstAmount || 
                                                            (selectedRecord.totalAmountWithGST && selectedRecord.totalAmountWithoutGST ? 
                                                                selectedRecord.totalAmountWithGST - selectedRecord.totalAmountWithoutGST : 0)
                                                        )}
                                                    </div>
                                                </div>
                                                <div className={`grid grid-cols-2 gap-2 pt-2 ${isDarkMode ? 'border-t border-blue-700' : 'border-t border-blue-200'}`}>
                                                    <div className={`text-sm font-semibold ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>Final Amount:</div>
                                                    <div className={`text-base font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                                                        ₹{formatNumberWithCommas(selectedRecord.totalAmountWithGST || selectedRecord.finalAmount || selectedRecord.totalAmount || 0)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Remarks */}
                                {selectedRecord.remarks && (
                                    <div className={`${isDarkMode ? 'bg-yellow-900/20 border-yellow-700' : 'bg-yellow-50 border-yellow-200'} border rounded-lg p-4`}>
                                        <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-yellow-400' : 'text-yellow-800'} flex items-center mb-2`}>
                                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                                            </svg>
                                            Remarks
                                        </h3>
                                        <p className={`text-sm ${isDarkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>{selectedRecord.remarks}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                        {/* End of Content - Scrollable */}

                    {/* Footer */}
                    <div className={`${isDarkMode ? 'bg-gray-750 border-gray-600' : 'bg-gray-50 border-gray-200'} border-t p-4 flex justify-end gap-3 flex-shrink-0 rounded-b-xl`}>
                        <button
                                onClick={closeModal}
                                className={`px-4 py-2 border ${isDarkMode ? 'border-gray-600 bg-gray-700 hover:bg-gray-600 text-white' : 'border-gray-300 bg-white hover:bg-gray-50 text-gray-700'} rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 ${isDarkMode ? 'focus:ring-gray-500' : 'focus:ring-gray-500'} flex items-center transition-colors shadow-sm`}
                            >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Dashboard;